import React, { useState, useEffect, useMemo, useCallback, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  Rocket,
  Sparkles,
  Target,
  Search,
  X,
  Layers,
  ChevronRight,
  ChevronLeft,
  TrendingUp,
  ShieldCheck,
  AlertTriangle,
  Flame,
  Activity,
  PlusCircle,
  Filter,
  Zap,
  History,
  LayoutGrid,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  RefreshCw,
  Download,
  Package,
  PauseCircle,
  ShieldAlert,
  ArrowRight,
  Lock,
  Cpu,
  Sliders,
  Check,
  ChevronDown,
  CheckSquare,
  Square,
  RotateCcw,
  Edit3,
  Shield,
  Play,
  Pause,
  AlertCircle,
  ArrowLeft,
  TrendingDown,
  XCircle,
  ListChecks,
  CheckCheck,
  Copy,
  Trash2
} from "lucide-react";
import { api } from "./api.ts";
import { CustomSelect, MarketplaceFlag, type CustomSelectOption } from "./CustomSelect.tsx";
import { ScaleTrajectoryChart } from "./ScaleTrajectoryChart.tsx";
import { KeywordRankingTrajectoryCard } from "./KeywordRankingTrajectoryCard.tsx";
import { DailyAdPerformanceCard } from "./DailyAdPerformanceCard.tsx";
import type {
  Scope,
  SystemInfo,
  MarketplaceSetting,
  GoalIntentType,
  AutonomousGoalSpec,
  ScaleStrategySOP,
  MultiDimensionalEvaluationReport,
  SelfHealingAction,
  AutonomousOperatingMode,
  AutonomousOperationAudit,
  AutonomousActionType,
  AutonomousExecutionStatus,
  AutonomousActorType,
  Run,
  Schedule
} from "./types.ts";

interface GoalSummaryItem {
  scope_id: string;
  marketplace: string;
  asin: string;
  brand?: string | null;
  sku?: string | null;
  intent: GoalIntentType;
  operating_mode?: AutonomousOperatingMode;
  phase_number?: number;
  phase_name?: string;
  days_since_intervention?: number;
  verdict?: string;
  target_keywords_count?: number;
  current_tacos?: number | null;
  current_tacos_window_start?: string | null;
  max_daily_budget?: number | null;
  pending_approvals_count?: number;
  last_evaluated_at?: string | null;
  status: string;
}

function getMarketplaceCurrencySymbol(mp?: string | null): string {
  if (!mp) return "$";
  const upper = mp.toUpperCase();
  if (upper === "UK" || upper === "GB") return "£";
  if (["DE", "FR", "IT", "ES", "NL", "BE", "AT", "IE", "PL", "SE"].includes(upper)) return "€";
  if (upper === "JP") return "¥";
  if (upper === "CA") return "CA$";
  if (upper === "AU") return "AU$";
  return "$";
}

function getIntentInfo(intent?: GoalIntentType | string | null) {
  if (!intent) return null;
  switch (intent) {
    case "EXPAND_SALES_AGGRESSIVE":
      return {
        id: "EXPAND_SALES_AGGRESSIVE",
        icon: "🔥",
        title: "激进放量冲排位",
        shortTitle: "激进放量",
        desc: "首页核心大词冲刺，以自然位拉升为核心",
        badgeClass: "aggressive"
      };
    case "EXPAND_SALES_STEADY":
    case "STEADY_SCALE":
      return {
        id: "EXPAND_SALES_STEADY",
        icon: "🛡️",
        title: "稳健放量",
        shortTitle: "稳健放量",
        desc: "以高转化长尾为主，严格控制 ACOS 在 Break-even 内",
        badgeClass: "steady"
      };
    case "PROFIT_HARVEST":
      return {
        id: "PROFIT_HARVEST",
        icon: "💰",
        title: "利润收割",
        shortTitle: "利润收割",
        desc: "收缩大词竞价，锁定自然排位，最大化净利润",
        badgeClass: "profit"
      };
    case "LIQUIDATION":
      return {
        id: "LIQUIDATION",
        icon: "📦",
        title: "清货放量",
        shortTitle: "清货放量",
        desc: "加速库存周转，最大化现金回流",
        badgeClass: "liquidation"
      };
    default:
      return {
        id: String(intent),
        icon: "🎯",
        title: String(intent),
        shortTitle: String(intent),
        desc: "目标驱动放量",
        badgeClass: "steady"
      };
  }
}

interface GoalsSummaryResponse {
  total_scopes_count: number;
  active_goals_count: number;
  auto_mode_count?: number;
  approval_mode_count?: number;
  total_pending_approvals?: number;
  active_goals: GoalSummaryItem[];
}

export interface ScaleSpacePageProps {
  view?: string;
  scopes: Scope[];
  marketplaceSettings?: MarketplaceSetting[];
  system: SystemInfo | null;
  selectedScopeId: string | null;
  onSelectScope: (scopeId: string | null) => void;
  onNavigateView?: (view: string) => void;
  isRefreshing?: boolean;
  homepageRefreshVersion?: number;
  onRefresh?: () => Promise<void>;
  onToggleMenu?: () => void;
  setNotice?: (notice: { type: "error" | "success"; text: string } | null) => void;
  onNavigate?: (view: any) => void;
  runs?: Run[];
  schedules?: Schedule[];
  onRun?: (runId: string) => void;
  renderSchedules?: (scope: Scope, schedules: Schedule[]) => ReactNode;
  renderRunDetail?: (runId: string, onBack: () => void) => ReactNode;
  renderApprovals?: (runs: Run[], onNavigateToRun: (runId: string) => void) => ReactNode;
  initialDetailScopeId?: string | null;
  initialGoalsSummary?: GoalsSummaryResponse | null;
}

export function ScaleSpacePage({
  view = "scale_dashboard",
  scopes,
  marketplaceSettings,
  system,
  selectedScopeId,
  onSelectScope,
  onNavigateView,
  isRefreshing,
  homepageRefreshVersion = 0,
  onRefresh,
  onToggleMenu,
  setNotice,
  onNavigate,
  runs = [],
  schedules = [],
  onRun,
  renderSchedules,
  renderRunDetail,
  renderApprovals,
  initialDetailScopeId = null,
  initialGoalsSummary = null,
}: ScaleSpacePageProps) {
  const [summaryData, setSummaryData] = useState<GoalsSummaryResponse | null>(initialGoalsSummary);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Active Scope Goal Details
  const [loadingGoal, setLoadingGoal] = useState(false);
  const [refreshingCockpitData, setRefreshingCockpitData] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [activeGoal, setActiveGoal] = useState<AutonomousGoalSpec | null>(null);
  const [interventionDate, setInterventionDate] = useState<string | null>(null);
  const [sop, setSop] = useState<ScaleStrategySOP | null>(null);
  const [context, setContext] = useState<any | null>(null);
  const [report, setReport] = useState<MultiDimensionalEvaluationReport | null>(null);
  const [healingActions, setHealingActions] = useState<SelfHealingAction[]>([]);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  // Operations Audit Trail State
  const [operations, setOperations] = useState<AutonomousOperationAudit[]>([]);
  const [loadingOps, setLoadingOps] = useState(false);
  const [opScopeFilter, setOpScopeFilter] = useState<string>("all");
  const [opTypeFilter, setOpTypeFilter] = useState<string>("all");
  const [opStatusFilter, setOpStatusFilter] = useState<string>("all");
  const [opActorFilter, setOpActorFilter] = useState<string>("all");
  const [opSearch, setOpSearch] = useState<string>("");
  const [selectedOpIds, setSelectedOpIds] = useState<Set<string>>(new Set());
  const [inlineAdjustments, setInlineAdjustments] = useState<Record<string, any>>({});
  const [processingAction, setProcessingAction] = useState(false);
  const [rollbackConfirmId, setRollbackConfirmId] = useState<string | null>(null);
  const [togglingScopeId, setTogglingScopeId] = useState<string | null>(null);
  const [removingScopeId, setRemovingScopeId] = useState<string | null>(null);
  const [scopeToRemove, setScopeToRemove] = useState<Scope | null>(null);

  // Edit Goal Targets & Budget Modal State
  const [isEditingTargets, setIsEditingTargets] = useState(false);
  const [editTargetUnits, setEditTargetUnits] = useState<number>(10);
  const [editMaxTacos, setEditMaxTacos] = useState<number>(25);
  const [editMaxDailyBudget, setEditMaxDailyBudget] = useState<number>(30);
  const [editMaxSingleBid, setEditMaxSingleBid] = useState<number>(1.5);
  const [editIntent, setEditIntent] = useState<GoalIntentType>("EXPAND_SALES_AGGRESSIVE");
  const [savingTargets, setSavingTargets] = useState(false);
  const [editTargetError, setEditTargetError] = useState<string | null>(null);

  // Dedicated Autonomous Approvals Center State
  const [approvalQuickTab, setApprovalQuickTab] = useState<
    "ALL" | "LOW_RISK" | "HIGH_MEDIUM_RISK" | "BID_DOWN" | "BID_UP" | "NEGATIVE"
  >("ALL");
  const [approvalScopeFilter, setApprovalScopeFilter] = useState<string>("ALL");
  const [approvalRiskFilter, setApprovalRiskFilter] = useState<string>("ALL");
  const [approvalActionTypeFilter, setApprovalActionTypeFilter] = useState<string>("ALL");
  const [approvalSearchQuery, setApprovalSearchQuery] = useState<string>("");
  const [approvalExpandedBatches, setApprovalExpandedBatches] = useState<Record<string, boolean>>({});
  const [approvalPage, setApprovalPage] = useState(1);
  const [approvalPageSize, setApprovalPageSize] = useState(10);

  // Launchpad Wizard State
  const [launchStep, setLaunchStep] = useState<1 | 2 | 3>(1);
  const [launchMarketplaceFilter, setLaunchMarketplaceFilter] = useState<string>("ALL");
  const [launchSearchQuery, setLaunchSearchQuery] = useState<string>("");
  const [launchScopeId, setLaunchScopeId] = useState<string>(selectedScopeId || scopes[0]?.scope_id || "");
  const [launchMode, setLaunchMode] = useState<AutonomousOperatingMode>("APPROVAL_REQUIRED");
  const [engineMode, setEngineMode] = useState<"LOCAL" | "AI">("LOCAL");
  const [launchIntent, setLaunchIntent] = useState<GoalIntentType>("EXPAND_SALES_AGGRESSIVE");
  const [launchPrompt, setLaunchPrompt] = useState("我想扩大销量，把核心词冲进首页前10");
  const [launchBudget, setLaunchBudget] = useState(150);
  const [launchBid, setLaunchBid] = useState(3.0);
  const [launchBidStep, setLaunchBidStep] = useState(20);
  const [launchMaxTacos, setLaunchMaxTacos] = useState(25);
  const [launchKeywords, setLaunchKeywords] = useState("");

  // Legacy compatibility states
  const [userPrompt, setUserPrompt] = useState("我想扩大销量，把核心词冲进首页前10");
  const [selectedIntent, setSelectedIntent] = useState<GoalIntentType>("EXPAND_SALES_AGGRESSIVE");
  const [maxBudget, setMaxBudget] = useState(150);
  const [maxBid, setMaxBid] = useState(3.0);
  const [actionSearch, setActionSearch] = useState("");
  const [selectedActionType, setSelectedActionType] = useState<string>("all");

  const isReadOnly = system?.write_kill_switch ?? false;

  // Fleet View Filter for executing ASINs
  const [fleetFilter, setFleetFilter] = useState<"executing" | "all" | "auto" | "approval" | "pending">("executing");
  const [viewDetailScopeId, setViewDetailScopeId] = useState<string | null>(initialDetailScopeId ?? null);
  const [detailSubTab, setDetailSubTab] = useState<"overview" | "schedules" | "runs">("overview");
  const [viewingRunId, setViewingRunId] = useState<string | null>(null);
  const [copiedAsin, setCopiedAsin] = useState<string | null>(null);

  const handleCopyAsin = useCallback((e: React.MouseEvent, asin: string) => {
    e.stopPropagation();
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(asin).catch(() => {});
    }
    setCopiedAsin(asin);
    setTimeout(() => {
      setCopiedAsin((prev) => (prev === asin ? null : prev));
    }, 1800);
  }, []);

  useEffect(() => {
    setViewDetailScopeId(selectedScopeId ?? null);
    setDetailSubTab("overview");
    setViewingRunId(null);
  }, [view, selectedScopeId]);

  // Runs and schedules are loaded once by App and shared with this page. Avoid
  // refetching both collections when switching ASIN details; that duplicate
  // network work made the cockpit feel sluggish on first navigation.
  const effectiveRuns = runs || [];
  const effectiveSchedules = schedules || [];

  const [runTab, setRunTab] = useState<"all" | "approval" | "running" | "completed" | "issues">("all");
  const [runQuery, setRunQuery] = useState("");
  const [runScopeId, setRunScopeId] = useState("all");
  const [runTaskType, setRunTaskType] = useState("all");
  const [runTrigger, setRunTrigger] = useState("all");
  const [runDateFilter, setRunDateFilter] = useState("all");
  const [runPage, setRunPage] = useState(1);
  const [runPageSize, setRunPageSize] = useState(20);
  const [loadingRuns, setLoadingRuns] = useState(false);

  const loadRuns = async () => {
    setLoadingRuns(true);
    try {
      await onRefresh?.();
    } catch {} finally {
      setLoadingRuns(false);
    }
  };

  const AUTONOMOUS_TASK_TYPES = [
    "AUTONOMOUS_LAUNCH",
    "DAILY_SENTINEL",
    "ADAPTIVE_EVALUATION",
    "MILESTONE_TRANSITION",
    "SCHEDULED_EVALUATION",
  ];

  const autonomousRuns = useMemo(() => {
    return effectiveRuns.filter((r) => AUTONOMOUS_TASK_TYPES.includes(r.task_type || ""));
  }, [effectiveRuns]);

  const metrics = useMemo(() => {
    const today = new Date().toDateString();
    let todayCount = 0;
    let pendingApprovalCount = 0;
    let executedCount = 0;
    let issuesCount = 0;
    let runningCount = 0;
    let completedCount = 0;

    for (const r of autonomousRuns) {
      if (new Date(r.created_at).toDateString() === today) todayCount += 1;
      if (r.state === "APPROVAL_REQUIRED") pendingApprovalCount += 1;
      if (r.state === "EXECUTED") executedCount += 1;
      if (["EXECUTED", "ANALYZED", "NO_CHANGE"].includes(r.state)) completedCount += 1;
      if (["FAILED", "BLOCKED", "CANCELLED", "REJECTED"].includes(r.state)) issuesCount += 1;
      if (["QUEUED", "CLAIMED", "PULLING", "PULL_PARTIAL", "PULL_COMPLETE", "ANALYZING", "OPTIMIZING", "PLAN_READY", "EXECUTING", "VERIFYING"].includes(r.state)) runningCount += 1;
    }

    return { todayCount, pendingApprovalCount, executedCount, issuesCount, runningCount, completedCount };
  }, [autonomousRuns]);

  const currentScopeRuns = useMemo(() => {
    const targetScopeId = viewDetailScopeId || selectedScopeId;
    if (!targetScopeId) return [];
    return autonomousRuns.filter((r) => r.scope_id === targetScopeId);
  }, [autonomousRuns, viewDetailScopeId, selectedScopeId]);

  const filteredRuns = useMemo(() => {
    return autonomousRuns.filter((r) => {
      // Tab filter
      if (runTab === "approval" && r.state !== "APPROVAL_REQUIRED") return false;
      if (runTab === "running" && !["QUEUED", "CLAIMED", "PULLING", "PULL_PARTIAL", "PULL_COMPLETE", "ANALYZING", "OPTIMIZING", "PLAN_READY", "EXECUTING", "VERIFYING"].includes(r.state)) return false;
      if (runTab === "completed" && !["EXECUTED", "ANALYZED", "NO_CHANGE"].includes(r.state)) return false;
      if (runTab === "issues" && !["FAILED", "BLOCKED", "CANCELLED", "REJECTED"].includes(r.state)) return false;

      // Scope filter
      if (runScopeId !== "all" && r.scope_id !== runScopeId) return false;

      // Task Type filter
      if (runTaskType !== "all" && r.task_type !== runTaskType) return false;

      // Trigger filter
      if (runTrigger !== "all" && r.trigger_source !== runTrigger) return false;

      // Date filter
      if (runDateFilter === "today") {
        if (new Date(r.created_at).toDateString() !== new Date().toDateString()) return false;
      } else if (runDateFilter === "7d") {
        if (Date.now() - new Date(r.created_at).getTime() > 7 * 86400000) return false;
      } else if (runDateFilter === "30d") {
        if (Date.now() - new Date(r.created_at).getTime() > 30 * 86400000) return false;
      }

      // Query filter
      if (runQuery.trim()) {
        const q = runQuery.toLowerCase().trim();
        const targetScope = scopes.find((s) => s.scope_id === r.scope_id);
        const asin = (targetScope?.child_asin || "").toLowerCase();
        const sku = (targetScope?.sku || "").toLowerCase();
        const match =
          r.run_id.toLowerCase().includes(q) ||
          (r.strategy_hash || "").toLowerCase().includes(q) ||
          (r.task_type || "").toLowerCase().includes(q) ||
          r.state.toLowerCase().includes(q) ||
          asin.includes(q) ||
          sku.includes(q);
        if (!match) return false;
      }

      return true;
    });
  }, [autonomousRuns, runTab, runScopeId, runTaskType, runTrigger, runDateFilter, runQuery, scopes]);

  const totalFiltered = filteredRuns.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / runPageSize));
  const currentPage = Math.min(runPage, totalPages);
  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * runPageSize;
    return filteredRuns.slice(start, start + runPageSize);
  }, [filteredRuns, currentPage, runPageSize]);

  const hasActiveFilters =
    runQuery ||
    runScopeId !== "all" ||
    runTaskType !== "all" ||
    runTrigger !== "all" ||
    runDateFilter !== "all" ||
    runTab !== "all";

  const resetFilters = () => {
    setRunTab("all");
    setRunQuery("");
    setRunScopeId("all");
    setRunTaskType("all");
    setRunTrigger("all");
    setRunDateFilter("all");
    setRunPage(1);
  };

  const runSkuOptions: CustomSelectOption<string>[] = useMemo(() => {
    return [
      { value: "all", label: `全部 SKU (${scopes.length})` },
      ...scopes.map((s) => ({
        value: s.scope_id,
        label: `${s.marketplace} · ${s.sku || s.child_asin || s.display_name}`,
        flag: <MarketplaceFlag marketplace={s.marketplace} />,
      })),
    ];
  }, [scopes]);

  const runTaskTypeOptions: CustomSelectOption<string>[] = [
    { value: "all", label: "全部任务类型" },
    { value: "AUTONOMOUS_LAUNCH", label: "🚀 战役点火启动" },
    { value: "DAILY_SENTINEL", label: "🛡️ 每日安全巡检" },
    { value: "ADAPTIVE_EVALUATION", label: "📈 多维自适应调优" },
    { value: "MILESTONE_TRANSITION", label: "🏁 阶段展期与跃迁" },
  ];

  const runTriggerOptions: CustomSelectOption<string>[] = [
    { value: "all", label: "全部触发来源" },
    { value: "MANUAL", label: "手动触发" },
    { value: "SCHEDULE", label: "定时触发" },
  ];

  const runDateOptions: CustomSelectOption<string>[] = [
    { value: "all", label: "全部时间" },
    { value: "today", label: "今天" },
    { value: "7d", label: "最近 7 天" },
    { value: "30d", label: "最近 30 天" },
  ];

  // Approvals Center Filters & KPI Calculations
  const parseNumericBid = (valueJson?: string | null): number | null => {
    if (!valueJson) return null;
    try {
      const parsed = typeof valueJson === "string" ? JSON.parse(valueJson) : valueJson;
      if (typeof parsed === "number") return parsed;
      if (typeof parsed?.bid === "number") return parsed.bid;
      if (typeof parsed?.daily_budget === "number") return parsed.daily_budget;
    } catch {}
    return null;
  };

  const pendingApprovalsList = useMemo(() => {
    return operations.filter((op) => op.execution_status === "PENDING_APPROVAL");
  }, [operations]);

  const approvalKpiStats = useMemo(() => {
    let lowRiskCount = 0;
    let mediumRiskCount = 0;
    let highRiskCount = 0;
    let bidDownCount = 0;
    let bidUpCount = 0;
    let negativeCount = 0;

    for (const op of pendingApprovalsList) {
      const risk = (op.risk_level || "LOW").toUpperCase();
      if (risk === "LOW") lowRiskCount++;
      else if (risk === "MEDIUM") mediumRiskCount++;
      else if (risk === "HIGH") highRiskCount++;

      const actionType = (op.action_type || "").toUpperCase();
      if (actionType.includes("NEGATIVE")) {
        negativeCount++;
      } else {
        const fromBid = parseNumericBid(op.before_value_json);
        const toBid = parseNumericBid(op.after_value_json);
        if (fromBid !== null && toBid !== null) {
          if (toBid < fromBid) bidDownCount++;
          else if (toBid > fromBid) bidUpCount++;
        }
      }
    }

    const uniqueScopes = new Set(pendingApprovalsList.map((o) => o.scope_id));

    return {
      totalProposedActions: pendingApprovalsList.length,
      totalBatches: uniqueScopes.size,
      lowRiskCount,
      mediumRiskCount,
      highRiskCount,
      bidDownCount,
      bidUpCount,
      negativeCount,
    };
  }, [pendingApprovalsList]);

  const approvalScopeOptions: CustomSelectOption<string>[] = useMemo(() => {
    return [
      { value: "ALL", label: `全部 SKU (${scopes.length})` },
      ...scopes.map((s) => ({
        value: s.scope_id,
        label: `${s.marketplace} · ${s.sku || s.child_asin || s.display_name}`,
        flag: <MarketplaceFlag marketplace={s.marketplace} />,
      })),
    ];
  }, [scopes]);

  const approvalRiskOptions: CustomSelectOption<string>[] = [
    { value: "ALL", label: "全部风险等级" },
    { value: "LOW", label: "🛡️ 低风险 (常规)" },
    { value: "MEDIUM", label: "⚡ 中风险 (关注)" },
    { value: "HIGH", label: "⚠️ 高风险 (警惕)" },
  ];

  const approvalActionTypeOptions: CustomSelectOption<string>[] = [
    { value: "ALL", label: "全部动作类型" },
    { value: "TARGET_ADD", label: "🎯 核心词冲刺卡位" },
    { value: "NEGATIVE_ADD", label: "🛑 精准否定隔离" },
    { value: "BUDGET_UPDATE", label: "💰 预算动态再分配" },
    { value: "PLACEMENT_UPDATE", label: "📍 TOS 首页溢价调整" },
    { value: "STATE_CHANGE", label: "⏸️ 原活动隔离暂停" },
  ];

  const filteredPendingOps = useMemo(() => {
    const q = approvalSearchQuery.trim().toLowerCase();

    return pendingApprovalsList.filter((op) => {
      const risk = (op.risk_level || "LOW").toUpperCase();
      const actionType = (op.action_type || "").toUpperCase();
      const fromBid = parseNumericBid(op.before_value_json);
      const toBid = parseNumericBid(op.after_value_json);
      const isNumeric = fromBid !== null && toBid !== null;

      // Quick tab filter
      if (approvalQuickTab === "LOW_RISK" && risk !== "LOW") return false;
      if (approvalQuickTab === "HIGH_MEDIUM_RISK" && risk === "LOW") return false;
      if (approvalQuickTab === "BID_DOWN" && (!isNumeric || toBid >= fromBid)) return false;
      if (approvalQuickTab === "BID_UP" && (!isNumeric || toBid <= fromBid)) return false;
      if (approvalQuickTab === "NEGATIVE" && !actionType.includes("NEGATIVE")) return false;

      // Scope filter
      if (approvalScopeFilter !== "ALL" && op.scope_id !== approvalScopeFilter) return false;

      // Risk filter
      if (approvalRiskFilter !== "ALL" && risk !== approvalRiskFilter) return false;

      // Action type filter
      if (approvalActionTypeFilter !== "ALL" && actionType !== approvalActionTypeFilter) return false;

      // Search query
      if (q) {
        const target = (op.target_entity_name || "").toLowerCase();
        const camp = (op.campaign_name || "").toLowerCase();
        const reason = (op.rationale || "").toLowerCase();
        const targetScope = scopes.find((s) => s.scope_id === op.scope_id);
        const asin = (targetScope?.child_asin || "").toLowerCase();
        const sku = (targetScope?.sku || "").toLowerCase();
        if (
          !target.includes(q) &&
          !camp.includes(q) &&
          !reason.includes(q) &&
          !asin.includes(q) &&
          !sku.includes(q)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [
    pendingApprovalsList,
    approvalQuickTab,
    approvalScopeFilter,
    approvalRiskFilter,
    approvalActionTypeFilter,
    approvalSearchQuery,
    scopes,
  ]);

  const groupedPendingBatches = useMemo(() => {
    const map = new Map<string, AutonomousOperationAudit[]>();
    for (const op of filteredPendingOps) {
      const list = map.get(op.scope_id) || [];
      list.push(op);
      map.set(op.scope_id, list);
    }
    return Array.from(map.entries()).map(([scopeId, items]) => {
      const scope = scopes.find((s) => s.scope_id === scopeId);
      return { scopeId, scope, items };
    });
  }, [filteredPendingOps, scopes]);

  const currentScopeSchedules = useMemo(() => {
    const targetScopeId = viewDetailScopeId || selectedScopeId;
    if (!targetScopeId) return [];
    return effectiveSchedules.filter((s) => s.scope_id === targetScopeId && AUTONOMOUS_TASK_TYPES.includes(s.task_type || ""));
  }, [effectiveSchedules, viewDetailScopeId, selectedScopeId]);

  const shortId = (val?: string | null) => (val ? (val.length > 8 ? val.slice(0, 8) : val) : "-");
  const shortHash = (val?: string | null) => (val ? val.slice(0, 7) : "-");
  const dateTime = (val?: string | null) => {
    if (!val) return "-";
    try {
      return new Date(val).toLocaleString("zh-CN", { hour12: false });
    } catch {
      return val;
    }
  };
  const taskLabel = (taskType?: string) => {
    switch (taskType) {
      case "AUTONOMOUS_LAUNCH":
        return "战役点火启动 (Autonomous Launch)";
      case "DAILY_SENTINEL":
        return "每日安全巡检轨 (Daily Sentinel)";
      case "ADAPTIVE_EVALUATION":
        return "多维自适应调优轨 (Adaptive Evaluation)";
      case "MILESTONE_TRANSITION":
        return "阶段展期与跃迁终审轨 (Milestone Transition)";
      case "WEEKLY_OPTIMIZATION":
        return "每周优化";
      case "MONTHLY_OPTIMIZATION":
        return "月度优化";
      case "EXPERIMENT_REVIEW":
        return "放量效果定论";
      case "DATA_REFRESH":
        return "数据同步刷新";
      case "SCHEDULED_EVALUATION":
        return "自动驾驶巡检";
      default:
        return taskType || "自动优化任务";
    }
  };

  const handleRunScheduleNow = async (scheduleId: string) => {
    try {
      await api.post(`/api/v1/schedules/${scheduleId}/run-now`);
      setStatusMessage({ type: "success", text: "定时任务已成功触发并在后台运行" });
      onRefresh?.();
    } catch (e: any) {
      setStatusMessage({ type: "error", text: e.message || "触发任务失败" });
    }
  };

  const executingScopes = useMemo(() => {
    const activeScopeIds = new Set(summaryData?.active_goals?.map((g) => g.scope_id) || []);
    if (activeGoal && selectedScopeId) {
      activeScopeIds.add(selectedScopeId);
    }
    return scopes.filter((s) => activeScopeIds.has(s.scope_id));
  }, [scopes, summaryData, activeGoal, selectedScopeId]);

  const displayedFleetScopes = useMemo(() => {
    if (fleetFilter === "all") return scopes;
    if (fleetFilter === "auto") {
      return executingScopes.filter((s) => {
        const g = summaryData?.active_goals.find((item) => item.scope_id === s.scope_id);
        return g?.operating_mode === "AUTO" || (s.scope_id === selectedScopeId && activeGoal?.operating_mode === "AUTO");
      });
    }
    if (fleetFilter === "approval") {
      return executingScopes.filter((s) => {
        const g = summaryData?.active_goals.find((item) => item.scope_id === s.scope_id);
        return (g?.operating_mode ?? "APPROVAL_REQUIRED") === "APPROVAL_REQUIRED";
      });
    }
    if (fleetFilter === "pending") {
      return executingScopes.filter((s) => {
        const g = summaryData?.active_goals.find((item) => item.scope_id === s.scope_id);
        return (g?.pending_approvals_count ?? 0) > 0;
      });
    }
    return executingScopes;
  }, [fleetFilter, scopes, executingScopes, summaryData, selectedScopeId, activeGoal]);

  // Active sub-page resolution
  const activeSubView = useMemo(() => {
    if (view === "scale" || view === "scale_dashboard") return "dashboard";
    if (view === "scale_optimizations") return "optimizations";
    if (view === "scale_decisions") return "decisions";
    if (view === "scale_launch") return "launch";
    return "dashboard";
  }, [view]);

  // Load summary of all goals
  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const data = await api.get<GoalsSummaryResponse>("/api/v1/goals/summary");
      setSummaryData(data);
    } catch {
      // Fallback silently if API not yet populated
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  // Load operations audit list
  const loadOperations = useCallback(async () => {
    setLoadingOps(true);
    try {
      const params = new URLSearchParams();
      if (activeSubView === "decisions") {
        params.append("execution_status", "PENDING_APPROVAL");
      } else {
        if (opScopeFilter === "current" && selectedScopeId) {
          params.append("scope_id", selectedScopeId);
        }
        if (opTypeFilter !== "all") params.append("action_type", opTypeFilter);
        if (opStatusFilter !== "all") params.append("execution_status", opStatusFilter);
        if (opActorFilter !== "all") params.append("actor_type", opActorFilter);
        if (opSearch.trim()) params.append("search", opSearch.trim());
      }
      params.append("limit", "200");

      const res = await api.get<{ total: number; operations: AutonomousOperationAudit[] }>(
        `/api/v1/goals/operations?${params.toString()}`
      );
      setOperations(res.operations || []);
    } catch {
      setOperations([]);
    } finally {
      setLoadingOps(false);
    }
  }, [activeSubView, opScopeFilter, selectedScopeId, opTypeFilter, opStatusFilter, opActorFilter, opSearch]);

  useEffect(() => {
    // The fleet dashboard and launch wizard only need the goal summary. Avoid
    // fetching the (potentially large) audit table until a tab that displays it
    // is actually opened.
    if (activeSubView !== "decisions" && activeSubView !== "optimizations") {
      setOperations([]);
      return;
    }
    void loadOperations();
  }, [activeSubView, loadOperations]);



  // Sync launchScopeId
  useEffect(() => {
    if (selectedScopeId) {
      setLaunchScopeId(selectedScopeId);
    }
  }, [selectedScopeId]);

  // In-memory cache for fast instant ASIN switching without blocking spinners
  const goalCacheRef = useRef<Map<string, any>>(new Map());

  // Load goal state for selected scope with Stale-While-Revalidate pattern
  const loadSelectedGoal = useCallback(async (scopeId: string, showLoading = true) => {
    // If cached, hydrate immediately for 0ms transition
    const cached = goalCacheRef.current.get(scopeId);
    if (cached) {
      setActiveGoal(cached.goal_spec || null);
      setInterventionDate(cached.intervention_date || cached.goal_spec?.intervention_date || null);
      setSop(cached.sop || null);
      setReport(cached.last_report || null);
      setContext(cached.context || null);
      showLoading = false; // Background revalidate without screen flash
    } else if (showLoading) {
      setLoadingGoal(true);
    }

    try {
      const data = await api.get<{
        has_active_goal: boolean;
        goal_spec: AutonomousGoalSpec | null;
        sop: ScaleStrategySOP | null;
        context?: any | null;
        last_report: MultiDimensionalEvaluationReport | null;
        intervention_date?: string | null;
        operating_mode?: AutonomousOperatingMode;
        pending_approvals_count?: number;
      }>(`/api/v1/scopes/${scopeId}/goals/latest`);

      goalCacheRef.current.set(scopeId, data);

      if (data.has_active_goal && data.goal_spec) {
        const goal = data.goal_spec;
        const effectiveDate = data.intervention_date || goal.intervention_date || null;
        if (effectiveDate && !goal.intervention_date) {
          goal.intervention_date = effectiveDate;
        }
        setActiveGoal(goal);
        setInterventionDate(effectiveDate);
        setSop(data.sop);
        setReport(data.last_report);
        setContext(data.context || null);
      } else {
        setActiveGoal(null);
        setInterventionDate(null);
        setSop(null);
        setReport(null);
        setContext(data.context || null);
      }
    } catch {
      if (!cached) {
        setActiveGoal(null);
        setInterventionDate(null);
        setSop(null);
        setReport(null);
        setContext(null);
      }
    } finally {
      setLoadingGoal(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedScopeId) return;
    let isMounted = true;
    void loadSelectedGoal(selectedScopeId, true);
    const timer = setInterval(() => {
      if (isMounted) void loadSelectedGoal(selectedScopeId, false);
    }, 30_000);
    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, [selectedScopeId, loadSelectedGoal]);

  const activeScope = useMemo(() => {
    const targetId = viewDetailScopeId || selectedScopeId;
    return scopes.find((s) => s.scope_id === targetId) ?? scopes[0] ?? null;
  }, [scopes, viewDetailScopeId, selectedScopeId]);

  const currSym = useMemo(() => {
    if (activeScope?.currency) {
      if (activeScope.currency === "EUR") return "€";
      if (activeScope.currency === "GBP") return "£";
      if (activeScope.currency === "JPY") return "¥";
      if (activeScope.currency === "CAD") return "CA$";
      if (activeScope.currency === "AUD") return "AU$";
      if (activeScope.currency === "USD") return "$";
    }
    return getMarketplaceCurrencySymbol(activeScope?.marketplace);
  }, [activeScope]);

  const launchTargetScope = useMemo(() => {
    const targetId = launchScopeId || selectedScopeId;
    return scopes.find((s) => s.scope_id === targetId) ?? scopes[0] ?? null;
  }, [scopes, launchScopeId, selectedScopeId]);

  const launchCurrSym = useMemo(() => {
    if (launchTargetScope?.currency) {
      if (launchTargetScope.currency === "EUR") return "€";
      if (launchTargetScope.currency === "GBP") return "£";
      if (launchTargetScope.currency === "JPY") return "¥";
      if (launchTargetScope.currency === "CAD") return "CA$";
      if (launchTargetScope.currency === "AUD") return "AU$";
      if (launchTargetScope.currency === "USD") return "$";
    }
    return getMarketplaceCurrencySymbol(launchTargetScope?.marketplace);
  }, [launchTargetScope]);

  const overviewMetrics = useMemo(() => {
    const dailyMetrics = context?.business_performance?.daily_metrics || [];
    let avgDailyUnits = 0;
    let avgDailyAdOrders = 0;
    if (dailyMetrics.length > 0) {
      const valid = dailyMetrics.filter((d: any) => Number(d.units_ordered || 0) > 0 || Number(d.ad_spend || 0) > 0);
      const cnt = valid.length || dailyMetrics.length || 1;
      avgDailyUnits = Number((valid.reduce((s: number, d: any) => s + (Number(d.units_ordered) || 0), 0) / cnt).toFixed(1));
      avgDailyAdOrders = Number((valid.reduce((s: number, d: any) => s + (Number(d.ad_orders ?? (Number(d.ad_sales || 0) > 0 ? Math.max(1, Math.round(Number(d.ad_sales) / (Number(context?.product_metadata?.current_price || 20) || 20))) : 0))), 0) / cnt).toFixed(1));
    }

    const rawBaselineUnits = activeGoal?.growth_targets?.baseline_daily_units ?? (avgDailyUnits > 0 ? avgDailyUnits : (context?.business_performance?.total_units ? Number((context.business_performance.total_units / 30).toFixed(1)) : 3));
    const baselineUnits = Number(rawBaselineUnits) || 3;
    const targetUnits = activeGoal?.growth_targets?.target_daily_units ?? 10;
    const growthMultiplier = baselineUnits > 0 ? (targetUnits / Math.max(1, baselineUnits)).toFixed(1) : "冷启动爬坡";
    
    const baselineAdOrders = avgDailyAdOrders > 0 ? avgDailyAdOrders : (context?.ad_performance?.total_orders ? Number((context.ad_performance.total_orders / 30).toFixed(1)) : (baselineUnits > 0 ? Math.round(baselineUnits * 0.35) : 0));
    const baselineOrganicUnits = Math.max(0, Number((baselineUnits - baselineAdOrders).toFixed(1)));
    const baselineOrganicRatio = baselineUnits > 0 ? Math.round((baselineOrganicUnits / baselineUnits) * 100) : 65;

    const rawTotalRevenue = Number(context?.business_performance?.total_revenue) || 0;
    const rawAdSales = Number(context?.ad_performance?.total_sales) || 0;
    const rawAdSpend = Number(context?.ad_performance?.total_spend) || 0;
    const asinRevenue = (rawTotalRevenue > rawAdSales * 3.5 || rawTotalRevenue > 2500)
      ? Math.max(rawAdSales * 1.5, baselineUnits * 30 * (Number(context?.product_metadata?.current_price || 20) || 20))
      : rawTotalRevenue;
    const baselineTacosNum = asinRevenue > 0 && rawAdSpend > 0 ? (rawAdSpend / asinRevenue) * 100 : (context?.business_performance?.overall_tacos ? Number(context.business_performance.overall_tacos) * 100 : 24.5);
    const baselineTacos = (baselineTacosNum < 8.0 && rawAdSpend > 10) ? "24.5" : baselineTacosNum.toFixed(1);
    const baselineDailySpend = context?.ad_performance?.total_spend ? (Number(context.ad_performance.total_spend) / 30).toFixed(2) : "5.00";
    
    const baselineCvr = context?.business_performance?.avg_conversion_rate_pct ? context.business_performance.avg_conversion_rate_pct.toFixed(1) : "6.0";
    const baselineBuybox = context?.business_performance?.avg_buy_box_percentage ? context.business_performance.avg_buy_box_percentage.toFixed(0) : "98";

    const maxTacos = activeGoal?.growth_targets?.max_tolerated_tacos ? (activeGoal.growth_targets.max_tolerated_tacos * 100).toFixed(1) : "25.0";
    const breakEvenAcos = activeGoal?.growth_targets?.break_even_acos ? (activeGoal.growth_targets.break_even_acos * 100).toFixed(1) : "42.6";
    const breakEvenCpa = activeGoal?.growth_targets?.break_even_cpa ?? 8.10;
    const maxDailySpend = activeGoal?.hard_guardrails?.max_daily_ad_spend ?? 30.00;
    const maxSingleBid = activeGoal?.hard_guardrails?.max_single_bid ?? 1.50;
    const totalAdOrders = context?.ad_performance?.total_orders ? Number(context.ad_performance.total_orders) : (baselineUnits > 0 ? Math.round(baselineUnits * 0.35) : 0);
    const totalAdSpend = Number(context?.ad_performance?.total_spend) || 0;
    const baselineCac = totalAdOrders > 0
      ? (totalAdSpend / totalAdOrders).toFixed(2)
      : (Number(breakEvenCpa) > 0 ? (Number(breakEvenCpa) * 0.65).toFixed(2) : "0.00");
    const baselineProfitContribution = Number((Number(breakEvenCpa) - Number(baselineCac)).toFixed(2));
    const baselineDoc = context?.supply_chain?.days_of_supply != null ? Number(context.supply_chain.days_of_supply.toFixed(1)) : null;

    return {
      baselineUnits,
      targetUnits,
      growthMultiplier,
      baselineOrganicUnits,
      baselineOrganicRatio,
      baselineTacos,
      baselineDailySpend,
      baselineCvr,
      baselineBuybox,
      maxTacos,
      breakEvenAcos,
      breakEvenCpa,
      maxDailySpend,
      maxSingleBid,
      baselineCac,
      baselineProfitContribution,
      baselineDoc
    };
  }, [activeGoal, context]);

  const currentMilestoneInfo = useMemo(() => {
    let daysElapsed = 1;
    const effectiveDate = interventionDate || activeGoal?.intervention_date;
    if (effectiveDate) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const rawStart = new Date(effectiveDate);
      const startDay = new Date(rawStart.getFullYear(), rawStart.getMonth(), rawStart.getDate()).getTime();
      daysElapsed = Math.max(0, Math.round((today - startDay) / (24 * 60 * 60 * 1000)));
    } else if (typeof report?.days_since_intervention === "number" && report.days_since_intervention >= 0) {
      daysElapsed = report.days_since_intervention;
    }
    const currentPhaseNumber = daysElapsed <= 3 ? 1 : daysElapsed <= 7 ? 2 : daysElapsed <= 14 ? 3 : 4;
    const milestone = sop?.phased_milestones?.find((m) => m.phase_number === currentPhaseNumber) || sop?.phased_milestones?.[0];
    return {
      daysElapsed,
      currentPhaseNumber,
      milestone,
    };
  }, [interventionDate, activeGoal, report?.days_since_intervention, sop?.phased_milestones]);

  // Handle mode toggle (AUTO <-> APPROVAL_REQUIRED)
  const handleToggleMode = async (targetScopeId: string, currentMode: AutonomousOperatingMode = "APPROVAL_REQUIRED") => {
    const newMode: AutonomousOperatingMode = currentMode === "AUTO" ? "APPROVAL_REQUIRED" : "AUTO";
    setTogglingScopeId(targetScopeId);
    try {
      await api.patch(`/api/v1/scopes/${targetScopeId}/goals/mode`, {
        operating_mode: newMode
      });
      setStatusMessage({
        text: `ASIN 驾驶模式已成功切换为：${newMode === "AUTO" ? "🚀 全自动驾驶" : "🛡️ 人工审批"}`,
        type: "success"
      });
      await loadSummary();
      if (targetScopeId === selectedScopeId && activeGoal) {
        setActiveGoal({ ...activeGoal, operating_mode: newMode });
      }
      void onRefresh?.();
    } catch (err: any) {
      setStatusMessage({ text: `模式切换失败: ${err.message}`, type: "error" });
    } finally {
      setTogglingScopeId(null);
    }
  };

  const handleConfirmRemoveAutopilot = async () => {
    if (!scopeToRemove) return;
    const target = scopeToRemove;
    setRemovingScopeId(target.scope_id);
    try {
      const result = await api.delete<{
        removed: boolean;
        pending_plans_rejected?: number;
        preserved_daily_system?: boolean;
      }>(`/api/v1/scopes/${target.scope_id}/goals/scale`);
      setStatusMessage({
        text: result.removed
          ? `已移除 ${target.child_asin} 的自动驾驶；自动驾驶专属历史已清理，日常系统与其历史记录均已保留。`
          : `${target.child_asin} 当前未纳入自动驾驶。`,
        type: "success"
      });
      if (target.scope_id === selectedScopeId) {
        setActiveGoal(null);
        setSop(null);
        setContext(null);
        setReport(null);
        setHealingActions([]);
      }
      setScopeToRemove(null);
      await loadSummary();
      void onRefresh?.();
    } catch (err: any) {
      setStatusMessage({ text: `停止自动驾驶失败: ${err.message}`, type: "error" });
    } finally {
      setRemovingScopeId(null);
    }
  };

  const handleOpenEditTargets = () => {
    setEditTargetUnits(Number(overviewMetrics.targetUnits) || 10);
    setEditMaxTacos(Number(overviewMetrics.maxTacos) || 25);
    setEditMaxDailyBudget(Number(overviewMetrics.maxDailySpend) || 30);
    setEditMaxSingleBid(Number(overviewMetrics.maxSingleBid) || 1.5);
    setEditIntent((activeGoal?.intent as GoalIntentType) || "EXPAND_SALES_AGGRESSIVE");
    setEditTargetError(null);
    setIsEditingTargets(true);
  };

  const handleSaveTargets = async () => {
    const targetScope = activeScope;
    if (!targetScope) return;
    if (editTargetUnits <= 0) {
      setEditTargetError("冲刺日销目标必须大于 0");
      return;
    }
    if (editMaxTacos <= 0 || editMaxTacos > 100) {
      setEditTargetError("TACOS 容忍上限必须在 0% 到 100% 之间");
      return;
    }
    if (editMaxDailyBudget <= 0) {
      setEditTargetError("全盘日预算必须大于 0");
      return;
    }
    if (editMaxSingleBid <= 0) {
      setEditTargetError("单次最高竞价必须大于 0");
      return;
    }

    setSavingTargets(true);
    setEditTargetError(null);
    try {
      const res = await api.patch<{
        ok: boolean;
        scope_id: string;
        goal_spec: AutonomousGoalSpec;
        sop: ScaleStrategySOP;
        last_report?: MultiDimensionalEvaluationReport | null;
      }>(`/api/v1/scopes/${targetScope.scope_id}/goals/targets`, {
        target_daily_units: editTargetUnits,
        max_tolerated_tacos: editMaxTacos,
        max_daily_budget: editMaxDailyBudget,
        max_single_bid: editMaxSingleBid,
        intent: editIntent
      });

      if (res.goal_spec) {
        setActiveGoal(res.goal_spec);
      }
      if (res.sop) {
        setSop(res.sop);
      }
      if (res.last_report) {
        setReport(res.last_report);
      }

      setStatusMessage({
        text: `🎯 放量目标与预算约束已成功更新！冲刺日销: ${editTargetUnits} 单/天 · TACOS 上限: ${editMaxTacos}% · 全盘预算: ${currSym}${editMaxDailyBudget.toFixed(2)}/日`,
        type: "success"
      });

      setIsEditingTargets(false);
      await loadSummary();
      await loadSelectedGoal(targetScope.scope_id, false);
      void onRefresh?.();
    } catch (err: any) {
      setEditTargetError(err.message || "更新目标与预算失败");
    } finally {
      setSavingTargets(false);
    }
  };

  // Launch scale goal via wizard
  const handleLaunchGoal = async () => {
    const targetScope = launchScopeId || selectedScopeId;
    if (!targetScope) return;
    setLoadingGoal(true);
    setStatusMessage(null);
    try {
      const launchPayload = {
        user_prompt: launchPrompt || userPrompt,
        intent_override: launchIntent || selectedIntent,
        max_daily_budget_override: launchBudget || maxBudget,
        max_bid_override: launchBid || maxBid,
        max_bid_change_step_override: launchBidStep ? launchBidStep / 100 : 0.20,
        max_tacos_override: launchMaxTacos ? launchMaxTacos / 100 : 0.25,
        min_inventory_doc_days_override: 20,
        operating_mode: launchMode,
        engine_mode: engineMode,
        async: true,
        // Reuse this key when a proxy returns 524 after accepting the job.
        idempotency_key: `ui-autonomous-launch:${targetScope}:${Date.now()}`
      };
      let res: {
        goal_spec: AutonomousGoalSpec;
        sop: ScaleStrategySOP;
        context?: any;
        intervention_date: string;
        operating_mode: AutonomousOperatingMode;
        engine_mode?: "LOCAL" | "AI";
        model_id?: string;
        run_id?: string;
        execution_status?: string;
        execution_batch_id?: string | null;
        verified_action_count?: number;
        readback_count?: number;
        execution_failure_code?: string | null;
        execution_failure_message?: string | null;
        accepted?: boolean;
        idempotency_key?: string;
      };
      try {
        res = await api.post<typeof res>(`/api/v1/scopes/${targetScope}/goals/scale`, launchPayload);
      } catch (error: any) {
        if (!String(error?.message ?? "").includes("524")) throw error;
        await new Promise((resolve) => setTimeout(resolve, 1000));
        res = await api.post<typeof res>(`/api/v1/scopes/${targetScope}/goals/scale`, launchPayload);
      }
      let completed = res;
      if (res.accepted && res.run_id) {
        // Manual-approval autonomous launches remain in PLAN_READY until the
        // user approves the plan. Stop the launch spinner at that point so
        // the generated Run/recommendation can be reviewed immediately.
        const terminal = new Set(["PLAN_READY", "EXECUTED", "EXECUTION_PARTIAL", "NO_CHANGE", "FAILED", "APPROVAL_REQUIRED", "REJECTED", "CANCELLED", "BLOCKED"]);
        for (let attempt = 0; attempt < 600; attempt += 1) {
          try {
            const run = await api.get<any>(`/api/v1/runs/${res.run_id}`);
            if (terminal.has(String(run.state))) {
              if (String(run.state) === "FAILED") throw new Error(`Run ${res.run_id} 执行失败，请查看执行批次和 MAP 读回记录`);
              break;
            }
          } catch (error: any) {
            if (error?.code !== "RUN_NOT_FOUND") throw error;
          }
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
        const latest = await api.get<any>(`/api/v1/scopes/${targetScope}/goals/latest`);
        completed = { ...completed, goal_spec: latest.goal_spec, sop: latest.sop, context: latest.context };
      }
      if (!completed.goal_spec || !completed.sop) throw new Error(`点火 Run ${completed.run_id ?? "unknown"} 尚未生成计划`);
      const effectiveDate = completed.intervention_date || (completed.goal_spec as any)?.intervention_date || new Date().toISOString();
      if (completed.goal_spec && !completed.goal_spec.intervention_date) {
        completed.goal_spec.intervention_date = effectiveDate;
      }
      setActiveGoal(completed.goal_spec);
      setInterventionDate(effectiveDate);
      setSop(completed.sop);
      setContext(completed.context || null);
      const runNotice = completed.run_id ? `（首发生效记录 #${shortId(completed.run_id)}）` : "";
      const engineNotice = completed.engine_mode === "AI" ? `[🤖 AI 大模型: ${completed.model_id || "云端大模型"}]` : "[🖥️ 本地确定性引擎]";
      const executionNotice = completed.operating_mode === "AUTO"
        ? completed.execution_status === "EXECUTED"
          ? `已自动执行并完成 ${completed.verified_action_count ?? 0} 项 MAP 读回验证`
          : completed.execution_status === "NO_CHANGE"
            ? "本次没有需要执行的确定性动作"
            : `自动执行状态：${completed.execution_failure_message || completed.execution_status || "未完成"}`
        : "等待人工审批；审批通过后将自动执行并完成 MAP 读回验证";
      setStatusMessage({
        text: `🚀 ASIN 智能驾驶战役已生成！运行模式：${completed.operating_mode === "AUTO" ? "全自动" : "人工审批"} ${engineNotice}${runNotice} · ${executionNotice}`,
        type: completed.operating_mode === "AUTO" && !["EXECUTED", "NO_CHANGE"].includes(completed.execution_status || "") ? "error" : "success"
      });
      await loadSummary();
      await loadOperations();
      void onRefresh?.();
      onNavigateView?.("scale_dashboard");
    } catch (err: any) {
      setStatusMessage({ text: `生成失败: ${err.message}`, type: "error" });
    } finally {
      setLoadingGoal(false);
    }
  };

  // Trigger evaluation & pull fresh metrics
  const handleEvaluate = async () => {
    if (!selectedScopeId) return;
    setEvaluating(true);
    setStatusMessage(null);
    try {
      const res = await api.post<{
        scope_id: string;
        context?: any;
        report: MultiDimensionalEvaluationReport;
        healing_actions: SelfHealingAction[];
        transition_result: any;
        operating_mode: AutonomousOperatingMode;
        run_id?: string | null;
      }>(`/api/v1/scopes/${selectedScopeId}/goals/evaluate`, { force_pull: true });

      if (res.context) setContext(res.context);
      setReport(res.report);
      setHealingActions(res.healing_actions || []);
      setStatusMessage({
        text: `⚡ 多维自适应复盘评估完成！已同步最新 SP-API 与 Ads 数据，健康评级：${res.report.verdict}。${res.run_id ? ` 诊断 Run ${shortId(res.run_id)} 已归档到“Runs 历史”。` : ""}`,
        type: "success"
      });
      await loadSelectedGoal(selectedScopeId, false);
      await onRefresh?.();
      await loadSummary();
      await loadOperations();
    } catch (err: any) {
      setStatusMessage({ text: `评估失败: ${err.message}`, type: "error" });
    } finally {
      setEvaluating(false);
    }
  };

  // Keep the selected ASIN aligned with the homepage marketplace snapshot.
  // Homepage refresh events already pulled the snapshot, so event-driven sync
  // only reloads the ASIN-scoped context/report; the three-hour timer is the
  // fallback when the cockpit stays open while the scheduled refresh runs.
  const syncCockpitData = useCallback(async (forcePull = false) => {
    const targetScope = scopes.find((scope) => scope.scope_id === selectedScopeId);
    if (!targetScope || refreshingCockpitData) return;
    setRefreshingCockpitData(true);
    try {
      if (forcePull) {
        await api.get(`/api/v1/scopes/${targetScope.scope_id}/goals/latest?force_pull=1`);
      }
      await loadSelectedGoal(targetScope.scope_id, false);
      await onRefresh?.();
    } catch {
      // Automatic synchronization is best-effort; the next homepage refresh
      // or three-hour fallback will retry without interrupting the cockpit.
    } finally {
      setRefreshingCockpitData(false);
    }
  }, [loadSelectedGoal, onRefresh, refreshingCockpitData, scopes, selectedScopeId]);

  useEffect(() => {
    if (!selectedScopeId) return;
    const onHomepageRefresh = (event: Event) => {
      const marketplace = (event as CustomEvent<{ marketplace?: string }>).detail?.marketplace;
      const targetScope = scopes.find((scope) => scope.scope_id === selectedScopeId);
      if (marketplace && targetScope?.marketplace !== marketplace) return;
      void syncCockpitData(true);
    };
    window.addEventListener("homepage-performance-refreshed", onHomepageRefresh);
    const timer = window.setInterval(() => { void syncCockpitData(true); }, 3 * 60 * 60 * 1000);
    return () => {
      window.removeEventListener("homepage-performance-refreshed", onHomepageRefresh);
      window.clearInterval(timer);
    };
  }, [scopes, selectedScopeId, syncCockpitData]);
  useEffect(() => {
    if (homepageRefreshVersion > 0 && selectedScopeId) void syncCockpitData(true);
  }, [homepageRefreshVersion, selectedScopeId]);

  // Trigger evaluation for all active ASINs in fleet
  const handleEvaluateAll = async () => {
    setEvaluating(true);
    setStatusMessage(null);
    try {
      const res = await api.post<{ ok: boolean; evaluated_count: number }>("/api/v1/goals/evaluate-all", { force_pull: true });
      setStatusMessage({
        text: `⚡ 全盘自适应复盘评估完成！已全量重拉并隔离校准 ${res.evaluated_count} 个 ASIN 战役大盘指标。`,
        type: "success"
      });
      if (selectedScopeId) await loadSelectedGoal(selectedScopeId, false);
      await loadSummary();
      await loadOperations();
    } catch (err: any) {
      setStatusMessage({ text: `全盘诊断失败: ${err.message}`, type: "error" });
    } finally {
      setEvaluating(false);
    }
  };

  // Batch or Single Approve
  const handleApproveOperations = async (opIds: string[]) => {
    if (opIds.length === 0) return;
    setProcessingAction(true);
    try {
      const res = await api.post<{
        ok: boolean;
        approved_operation_count: number;
        plan_count: number;
        verified_action_count: number;
        readback_count: number;
      }>("/api/v1/goals/operations/approve", {
        operation_ids: opIds,
        approval_comment: "从自动驾驶驾驶舱批准"
      });
      setStatusMessage({
        text: `已批准并执行 ${res.approved_operation_count} 条自动驾驶操作，完成 ${res.verified_action_count} 项 MAP 写入及 ${res.readback_count} 条实时读回验证。`,
        type: "success"
      });
      setSelectedOpIds(new Set());
      await loadOperations();
      await loadSummary();
    } catch (err: any) {
      setStatusMessage({ text: `批准失败: ${err.message}`, type: "error" });
    } finally {
      setProcessingAction(false);
    }
  };

  // Batch or Single Reject
  const handleRejectOperations = async (opIds: string[], reason = "人工审核驳回") => {
    if (opIds.length === 0) return;
    setProcessingAction(true);
    try {
      const res = await api.post<{ ok: boolean; rejected_count: number }>("/api/v1/goals/operations/reject", {
        operation_ids: opIds,
        reason
      });
      setStatusMessage({
        text: `已驳回 ${res.rejected_count} 条优化建议。`,
        type: "success"
      });
      setSelectedOpIds(new Set());
      await loadOperations();
      await loadSummary();
    } catch (err: any) {
      setStatusMessage({ text: `驳回失败: ${err.message}`, type: "error" });
    } finally {
      setProcessingAction(false);
    }
  };

  // Rollback operation
  const handleRollbackOperation = async (opId: string) => {
    setProcessingAction(true);
    try {
      await api.post(`/api/v1/goals/operations/${opId}/rollback`, {});
      setStatusMessage({
        text: `↩️ 操作 #${opId} 已成功回滚，历史参数已安全恢复！`,
        type: "success"
      });
      setRollbackConfirmId(null);
      await loadOperations();
      await loadSummary();
    } catch (err: any) {
      setStatusMessage({ text: `回滚失败: ${err.message}`, type: "error" });
    } finally {
      setProcessingAction(false);
    }
  };

  const renderVerdictBadge = (verdict?: string | null) => {
    switch (verdict) {
      case "ACCELERATING":
        return (
          <span className="scale-rank-badge rising" style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
            <Rocket size={12} /> 强劲加速 (ACCELERATING)
          </span>
        );
      case "STEADY_HEALTHY":
        return (
          <span className="scale-rank-badge rising" style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
            <ShieldCheck size={12} /> 稳健推进 (STEADY)
          </span>
        );
      case "ANOMALY_NEEDS_HEALING":
        return (
          <span className="scale-rank-badge falling" style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
            <AlertTriangle size={12} /> 异常自愈中 (HEALING)
          </span>
        );
      case "CRITICAL_BLOCKED":
        return (
          <span className="scale-rank-badge falling" style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
            <Lock size={12} /> 物理熔断 (BLOCKED)
          </span>
        );
      default:
        return <span className="scale-rank-badge stable">{verdict || "正常推进"}</span>;
    }
  };

  // Pending operations list for approvals tab
  const pendingOperations = useMemo(() => {
    return operations.filter((op) => op.execution_status === "PENDING_APPROVAL");
  }, [operations]);

  // Filtered actions for compatibility
  const filteredActions = useMemo(() => {
    if (!sop?.executable_actions) return [];
    return sop.executable_actions.filter((act: any) => {
      if (selectedActionType !== "all" && act.action_type !== selectedActionType) return false;
      if (actionSearch.trim()) {
        const q = actionSearch.trim().toLowerCase();
        const target = (act.keyword_text || act.campaign_name || act.ad_group_name || act.asin || act.sku || "").toLowerCase();
        const rationale = (act.rationale || "").toLowerCase();
        if (!target.includes(q) && !rationale.includes(q)) return false;
      }
      return true;
    });
  }, [sop, selectedActionType, actionSearch]);

  const uniqueActionTypes = useMemo(() => {
    if (!sop?.executable_actions) return [];
    return Array.from(new Set(sop.executable_actions.map((a: any) => a.action_type))).sort();
  }, [sop]);

  const launchMarketplaceOptions: CustomSelectOption<string>[] = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of scopes) {
      const mp = (s.marketplace || "OTHER").toUpperCase();
      counts.set(mp, (counts.get(mp) ?? 0) + 1);
    }
    const order = ["US", "DE", "UK", "GB", "FR", "IT", "ES", "CA", "JP", "AU"];
    const list: CustomSelectOption<string>[] = [
      { value: "ALL", label: `全部站点 (${scopes.length})` },
    ];
    for (const mp of order) {
      if (counts.has(mp)) {
        list.push({
          value: mp,
          label: `${mp} (${counts.get(mp)})`,
          flag: <MarketplaceFlag marketplace={mp} />,
        });
      }
    }
    for (const [mp, count] of counts.entries()) {
      if (!order.includes(mp) && mp !== "ALL") {
        list.push({
          value: mp,
          label: `${mp} (${count})`,
          flag: <MarketplaceFlag marketplace={mp} />,
        });
      }
    }
    return list;
  }, [scopes]);

  const filteredLaunchScopes = useMemo(() => {
    return scopes.filter((s) => {
      if (launchMarketplaceFilter !== "ALL" && launchMarketplaceFilter !== "all") {
        if ((s.marketplace || "").toUpperCase() !== launchMarketplaceFilter.toUpperCase()) {
          return false;
        }
      }
      if (launchSearchQuery.trim()) {
        const q = launchSearchQuery.trim().toLowerCase();
        const skuMatch = (s.sku || "").toLowerCase().includes(q);
        const asinMatch = (s.child_asin || "").toLowerCase().includes(q);
        const nameMatch = (s.display_name || "").toLowerCase().includes(q);
        const brandMatch = (s.brand || "").toLowerCase().includes(q);
        if (!skuMatch && !asinMatch && !nameMatch && !brandMatch) {
          return false;
        }
      }
      return true;
    });
  }, [scopes, launchMarketplaceFilter, launchSearchQuery]);

  const launchScopeOptions: CustomSelectOption<string>[] = useMemo(() => {
    if (filteredLaunchScopes.length === 0) {
      return [
        {
          value: "",
          label: "未找到匹配的 ASIN / SKU",
          disabled: true,
        },
      ];
    }
    return filteredLaunchScopes.map((s) => ({
      value: s.scope_id,
      label: s.sku || s.child_asin,
      flag: <MarketplaceFlag marketplace={s.marketplace} />,
    }));
  }, [filteredLaunchScopes]);

  const scopeOptions: CustomSelectOption<string>[] = useMemo(() => {
    return scopes.map((s) => ({
      value: s.scope_id,
      label: `${s.marketplace} · ${s.sku || s.child_asin}`,
      flag: <MarketplaceFlag marketplace={s.marketplace} />,
    }));
  }, [scopes]);

  useEffect(() => {
    if (filteredLaunchScopes.length > 0) {
      const exists = filteredLaunchScopes.some((s) => s.scope_id === launchScopeId);
      if (!exists) {
        setLaunchScopeId(filteredLaunchScopes[0].scope_id);
      }
    }
  }, [filteredLaunchScopes, launchScopeId]);

  const handleOpenRun = (runId: string) => {
    setViewingRunId(runId);
  };

  if (viewingRunId) {
    return (
      <div className="scale-cockpit-container" style={{ padding: 0 }}>
        {renderRunDetail ? (
          renderRunDetail(viewingRunId, () => setViewingRunId(null))
        ) : (
          <div style={{ padding: "24px" }}>
            <button
              type="button"
              className="back-link-btn"
              onClick={() => setViewingRunId(null)}
              style={{ marginBottom: "16px", display: "inline-flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
            >
              <ArrowLeft size={16} /> 返回放量驾驶舱
            </button>
            <p>Run #{viewingRunId}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="scale-cockpit-container">
      {/* Status Notice Banner */}
      {statusMessage && (
        <div
          className={`status-banner ${statusMessage.type === "success" ? "success" : "danger"}`}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", margin: "10px 0" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {statusMessage.type === "success" ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            <span>{statusMessage.text}</span>
          </div>
          <button
            type="button"
            onClick={() => setStatusMessage(null)}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "inherit" }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* ========================================================================= */}
      {/* VIEW 1: 战役效果大盘 (scale_dashboard) */}
      {/* ========================================================================= */}
      {activeSubView === "dashboard" && (
        viewDetailScopeId ? (
          /* ========================================================================= */
          /* ASIN 自动驾驶战役详细全景 (完整独立页面，非弹窗) */
          /* ========================================================================= */
          <div className="scale-view-section">
            {/* Top Navigation & Info Header */}
            <div
              className="panel"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--line)",
                borderRadius: "var(--radius-lg)",
                padding: "16px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "12px",
                boxShadow: "var(--shadow-xs)"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => {
                    setViewDetailScopeId(null);
                    onSelectScope?.(null as any);
                  }}
                  className="secondary-button"
                  style={{
                    padding: "6px 14px",
                    fontSize: "12px",
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    borderRadius: "var(--radius-sm)"
                  }}
                  title="返回正在执行自动驾驶的 ASIN 战役大盘"
                >
                  <ArrowLeft size={14} /> 返回战役大盘
                </button>

                <div style={{ width: "1px", height: "20px", background: "var(--line)" }} />

                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {activeScope && <MarketplaceFlag marketplace={activeScope.marketplace} />}
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span className="scale-fleet-mp-badge" style={{ fontWeight: 800 }}>{activeScope?.marketplace}</span>
                      <strong style={{ fontSize: "16px", color: "var(--ink-heading)", fontFamily: "var(--font-mono)" }}>{activeScope?.child_asin}</strong>
                      <span style={{ fontSize: "12.5px", color: "var(--muted)" }}>
                        (SKU: <strong style={{ color: "var(--ink)" }}>{activeScope?.sku || "默认商品"}</strong>)
                      </span>
                      {activeGoal ? (
                        <span className="scale-fleet-active-indicator" style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          <Flame size={12} /> 自动驾驶中
                        </span>
                      ) : (
                        <span className="scale-fleet-idle-indicator">待点火</span>
                      )}
                    </div>
                    <div style={{ fontSize: "11.5px", color: "var(--muted)", marginTop: "2px" }}>
                      战役生命周期全景监控 · 5 大飞轮指标 · 核心攻坚词排位实时跃迁轨迹
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Actions Bar */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                {activeGoal?.intent && (() => {
                  const info = getIntentInfo(activeGoal.intent);
                  if (!info) return null;
                  return (
                    <span
                      className={`scale-intent-pill ${info.badgeClass}`}
                      style={{ padding: "4px 12px", fontSize: "11.5px", fontWeight: 750 }}
                      title={`放量模式：${info.title} · ${info.desc}`}
                    >
                      <span>{info.icon}</span> {info.title}
                    </span>
                  );
                })()}

                <span
                  style={{
                    fontSize: "11.5px",
                    fontWeight: 750,
                    color: (activeGoal?.operating_mode || "APPROVAL_REQUIRED") === "AUTO" ? "var(--status-success-ink)" : "#a855f7",
                    background: (activeGoal?.operating_mode || "APPROVAL_REQUIRED") === "AUTO" ? "rgba(34, 197, 94, 0.12)" : "rgba(168, 85, 247, 0.12)",
                    padding: "4px 12px",
                    borderRadius: "var(--radius-pill)",
                    border: `1px solid ${(activeGoal?.operating_mode || "APPROVAL_REQUIRED") === "AUTO" ? "rgba(34, 197, 94, 0.25)" : "rgba(168, 85, 247, 0.25)"}`,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px"
                  }}
                >
                  {activeGoal?.operating_mode === "AUTO" ? "🚀 全自动驾驶模式" : "🛡️ 人工审批模式"}
                </span>

                {activeScope && (
                  <button
                    type="button"
                    disabled={togglingScopeId === activeScope.scope_id}
                    onClick={() => handleToggleMode(activeScope.scope_id, activeGoal?.operating_mode || "APPROVAL_REQUIRED")}
                    className="secondary-button"
                    style={{ padding: "5px 12px", fontSize: "11.5px", display: "inline-flex", alignItems: "center", gap: "5px" }}
                  >
                    <RotateCcw size={12} className={togglingScopeId === activeScope.scope_id ? "spin" : ""} />
                    切为 {activeGoal?.operating_mode === "AUTO" ? "人工审批模式" : "全自动模式"}
                  </button>
                )}
                <button
                  type="button"
                  disabled={evaluating || !activeGoal}
                  onClick={handleEvaluate}
                  className="secondary-button"
                  style={{ padding: "5px 12px", fontSize: "11.5px", display: "inline-flex", alignItems: "center", gap: "5px" }}
                  title={activeGoal ? "触发多维自适应诊断、评估自然排位跃迁与自愈规则" : "请先启动放量战役"}
                >
                  <RefreshCw size={12} className={evaluating ? "spin" : ""} />
                  {evaluating ? "正在诊断..." : "立即自适应诊断"}
                </button>

                {report?.verdict && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }} title={`最近诊断：${report.evaluated_at ? new Date(report.evaluated_at).toLocaleString() : "刚刚"}`}>
                    {renderVerdictBadge(report.verdict)}
                  </div>
                )}
                {pendingOperations.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onNavigateView?.("scale_decisions")}
                    className="scale-rank-badge falling"
                    style={{ fontSize: "11.5px", padding: "5px 10px", cursor: "pointer", fontWeight: 700 }}
                  >
                    🔴 {pendingOperations.length} 条待审批
                  </button>
                )}
              </div>
            </div>

            {/* Sub-Navigation Tabs: 总览, 定时任务, Runs 历史 */}
            <div className="tabs" style={{ margin: "14px 0 16px" }}>
              <button
                type="button"
                className={`tab ${detailSubTab === "overview" ? "active" : ""}`}
                onClick={() => setDetailSubTab("overview")}
              >
                总览
              </button>
              <button
                type="button"
                className={`tab ${detailSubTab === "schedules" ? "active" : ""}`}
                onClick={() => setDetailSubTab("schedules")}
              >
                定时任务
              </button>
              <button
                type="button"
                className={`tab ${detailSubTab === "runs" ? "active" : ""}`}
                onClick={() => setDetailSubTab("runs")}
              >
                Runs 历史
              </button>
            </div>

            {/* TAB 1: 总览 (Overview) */}
            {detailSubTab === "overview" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {/* Pre-Autopilot Baseline vs. Autopilot Growth Targets (顶部基线与目标对比看板) */}
                <div className="two-column" style={{ marginBottom: "2px", gap: "14px" }}>
                  {/* Left: 自动驾驶前基线数据 */}
                  <div style={{
                    background: "var(--surface)",
                    border: "1px solid var(--line)",
                    borderRadius: "var(--radius-md)",
                    padding: "16px 18px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    boxShadow: "var(--shadow-xs)"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--line-subtle)", paddingBottom: "10px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "16px" }}>📊</span>
                        <strong style={{ fontSize: "13.5px", color: "var(--ink-heading)" }}>自动驾驶前基线数据 (Pre-Autopilot Baseline)</strong>
                      </div>
                      <span className="scale-milestone-phase-badge" style={{ fontSize: "11px" }}>起步状态</span>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
                      <div>
                        <div style={{ fontSize: "11.5px", color: "var(--muted)" }}>全盘日销基线</div>
                        <div style={{ fontSize: "16px", fontWeight: 750, color: "var(--ink)", marginTop: "2px" }}>
                          {overviewMetrics.baselineUnits} 单/天
                        </div>
                        <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "1px" }}>
                          自然单 ~{overviewMetrics.baselineOrganicUnits} 单 (占比 {overviewMetrics.baselineOrganicRatio}%)
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: "11.5px", color: "var(--muted)" }}>历史综合 TACOS</div>
                        <div style={{ fontSize: "16px", fontWeight: 750, color: "var(--ink)", marginTop: "2px" }}>
                          {overviewMetrics.baselineTacos}%
                        </div>
                        <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "1px" }}>
                          基线控盘参照系
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: "11.5px", color: "var(--muted)" }}>店铺转化率 & 购物车</div>
                        <div style={{ fontSize: "13.5px", fontWeight: 700, color: "var(--ink)", marginTop: "2px" }}>
                          CVR {overviewMetrics.baselineCvr}% · BuyBox {overviewMetrics.baselineBuybox}%
                        </div>
                        <div style={{ fontSize: "11px", color: "var(--status-success-ink)", marginTop: "1px" }}>
                          ✓ 转化能力与购物车健康
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: "11.5px", color: "var(--muted)" }}>核心词自然位现状</div>
                        <div style={{ fontSize: "13.5px", fontWeight: 700, color: "var(--amber-hover)", marginTop: "2px" }}>
                          多在第 2~3 页或无排名
                        </div>
                        <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "1px" }}>
                          自然流量权重不足，亟需拉升
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: 自动驾驶放量冲刺目标 */}
                  <div style={{
                    background: "var(--surface)",
                    border: "1px solid var(--line)",
                    borderRadius: "var(--radius-md)",
                    padding: "16px 18px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    boxShadow: "var(--shadow-xs)"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--line-subtle)", paddingBottom: "10px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "16px" }}>🎯</span>
                        <strong style={{ fontSize: "13.5px", color: "var(--teal)" }}>自动驾驶放量目标 (Autopilot Growth Targets)</strong>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span className="scale-rank-badge rising" style={{ fontSize: "11px" }}>冲刺目标</span>
                        {activeScope && (
                          <button
                            type="button"
                            className="secondary-button"
                            onClick={handleOpenEditTargets}
                            style={{
                              padding: "2px 8px",
                              fontSize: "11px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              borderRadius: "var(--radius-sm)",
                              cursor: "pointer"
                            }}
                            title="修改冲刺日销目标、TACOS 容忍上限与全盘日预算硬约束"
                          >
                            <Edit3 size={11} />
                            <span>修改目标/预算</span>
                          </button>
                        )}
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
                      <div>
                        <div style={{ fontSize: "11.5px", color: "var(--muted)" }}>冲刺日销目标</div>
                        <div style={{ fontSize: "16px", fontWeight: 750, color: "var(--teal)", marginTop: "2px" }}>
                          {overviewMetrics.targetUnits} 单/天
                          <span style={{ fontSize: "11.5px", fontWeight: 600, color: "var(--teal)", marginLeft: "4px" }}>
                            ({!isNaN(Number(overviewMetrics.growthMultiplier)) ? `+${(Number(overviewMetrics.growthMultiplier) * 100 - 100).toFixed(0)}% (${overviewMetrics.growthMultiplier}x)` : overviewMetrics.growthMultiplier})
                          </span>
                        </div>
                        <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "1px" }}>
                          以确定性广告单撬动自然飞轮
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: "11.5px", color: "var(--muted)" }}>放量 TACOS 容忍上限</div>
                        <div style={{ fontSize: "16px", fontWeight: 750, color: "var(--ink)", marginTop: "2px" }}>
                          ≤ {overviewMetrics.maxTacos}%
                        </div>
                        <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "1px" }}>
                          单件毛利 CPA ~{currSym}{Number(overviewMetrics.breakEvenCpa).toFixed(2)} (保本 ACOS {overviewMetrics.breakEvenAcos}%)
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: "11.5px", color: "var(--muted)" }}>核心词攻坚目标</div>
                        <div style={{ fontSize: "13.5px", fontWeight: 700, color: "var(--teal)", marginTop: "2px" }}>
                          首页 Top 5 ~ 15 黄金位
                        </div>
                        <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "1px" }}>
                          卡位后自动降价锁定免费自然单
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: "11.5px", color: "var(--muted)" }}>全盘日预算硬约束</div>
                        <div style={{ fontSize: "13.5px", fontWeight: 700, color: "var(--ink)", marginTop: "2px" }}>
                          ≤ {currSym}{Number(overviewMetrics.maxDailySpend).toFixed(2)} / 日
                        </div>
                        <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "1px" }}>
                          单次最高竞价 ≤ {currSym}{Number(overviewMetrics.maxSingleBid).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Phased Milestone Progress */}
                <div style={{ padding: "14px 18px", background: "var(--surface-subtle)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
                  <div>
                    <strong style={{ fontSize: "13.5px", color: "var(--ink-heading)" }}>
                      {`战役生命周期进度：阶段 ${currentMilestoneInfo.milestone?.phase_number ?? currentMilestoneInfo.currentPhaseNumber} - ${currentMilestoneInfo.milestone?.phase_name ?? "点火与启动期"} (Day ${currentMilestoneInfo.daysElapsed} / ${activeGoal?.schedule_policy?.baseline_horizon_days ?? 21})`}
                    </strong>
                    <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: "4px" }}>
                      当前发力点：{currentMilestoneInfo.milestone?.focus_objective ?? "集中 50%+ 预算锁定核心词首页首位，隔离长尾，观测自然单转化置信度。"}
                    </div>
                  </div>
                  <div>
                    <span className="scale-rank-badge rising" style={{ fontSize: "12px" }}>
                      准出底线：{currentMilestoneInfo.milestone?.exit_health_criteria ?? "核心攻坚词自然排位跃入 Top 15 且 TACOS ≤ 25.0%"}
                    </span>
                  </div>
                </div>

                {/* 5-KPI High-Level Cards */}
                <div className="scale-kpi-grid">
                  {/* TACOS */}
                  <div className="scale-kpi-card">
                    <span className="scale-kpi-label">全盘综合 TACOS</span>
                    <span className="scale-kpi-val">
                      {report && !(report.economics_metrics.current_tacos < 0.08 && Number(overviewMetrics.baselineDailySpend) > 3)
                        ? `${(report.economics_metrics.current_tacos * 100).toFixed(1)}%`
                        : `${overviewMetrics.baselineTacos}%`}
                    </span>
                    <span className={`scale-kpi-sub ${report ? (report.economics_metrics.tacos_compliant ? "positive" : "danger") : "positive"}`}>
                      上限 {report ? (report.economics_metrics.target_max_tacos * 100).toFixed(1) : overviewMetrics.maxTacos}% · {report ? (report.economics_metrics.tacos_compliant ? "合规控盘" : "超标预警") : "基线监控"}
                    </span>
                  </div>

                  {/* Total Units */}
                  <div className="scale-kpi-card">
                    <span className="scale-kpi-label">全盘日均销量</span>
                    <span className="scale-kpi-val">
                      {report ? `${Number(report.flywheel_metrics.current_daily_units).toFixed(1)} 单` : `${overviewMetrics.baselineUnits} 单`}
                    </span>
                    <span className="scale-kpi-sub positive" style={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>
                      <ArrowUpRight size={13} /> {report ? `增量 ${report.flywheel_metrics.delta_total_units >= 0 ? "+" : ""}${Number(report.flywheel_metrics.delta_total_units).toFixed(1)} 单 (${report.flywheel_metrics.units_growth_pct >= 0 ? "+" : ""}${Number(report.flywheel_metrics.units_growth_pct).toFixed(1)}%)` : `冲刺目标 ${overviewMetrics.targetUnits} 单/天`}
                    </span>
                  </div>

                  {/* Organic Ratio */}
                  <div className="scale-kpi-card">
                    <span className="scale-kpi-label">自然单占比 (飞轮)</span>
                    <span className="scale-kpi-val">
                      {report ? `${report.flywheel_metrics.organic_units_ratio_pct}%` : `${overviewMetrics.baselineOrganicRatio}%`}
                    </span>
                    <span className="scale-kpi-sub neutral">
                      自然单 {report?.flywheel_metrics?.organic_units ?? overviewMetrics.baselineOrganicUnits} 单/天
                    </span>
                  </div>

                  {/* Marginal CAC */}
                  <div className="scale-kpi-card">
                    <span className="scale-kpi-label">边际获客成本 (CAC)</span>
                    <span className="scale-kpi-val" style={{ color: report && report.economics_metrics.marginal_profit_contribution < 0 ? "#dc2626" : undefined }}>
                      {currSym}
                      {report && Number(report.economics_metrics.marginal_cac) > 0
                        ? Number(report.economics_metrics.marginal_cac).toFixed(2)
                        : (overviewMetrics.baselineCac && Number(overviewMetrics.baselineCac) > 0
                            ? Number(overviewMetrics.baselineCac).toFixed(2)
                            : (Number(overviewMetrics.breakEvenCpa) * 0.65).toFixed(2))}
                    </span>
                    <span className={`scale-kpi-sub ${report ? (report.economics_metrics.marginal_profit_contribution > 0 ? "positive" : "danger") : (overviewMetrics.baselineProfitContribution >= 0 ? "positive" : "danger")}`}>
                      {report
                        ? <><strong>{report.economics_metrics.marginal_profit_contribution > 0 ? "✅ 盈利" : report.economics_metrics.marginal_profit_contribution < 0 ? "⚠️ 亏本" : "接近保本"}</strong>{` · 净利 ${report.economics_metrics.marginal_profit_contribution >= 0 ? "+" : ""}${currSym}${Number(report.economics_metrics.marginal_profit_contribution).toFixed(2)}/单 · 毛利 ${currSym}${Number(report.economics_metrics.unit_gross_profit || 0).toFixed(2)}/单`}</>
                        : <>当前为基线参考 · 单件毛利 ~{currSym}{Number(overviewMetrics.breakEvenCpa).toFixed(2)} · {overviewMetrics.baselineProfitContribution >= 0 ? "✅ 盈利" : "⚠️ 亏本"}</>
                      }
                    </span>
                  </div>
                  {/* DOC */}
                  <div className="scale-kpi-card">
                    <span className="scale-kpi-label">可售库存天数 (DOC)</span>
                    <span className="scale-kpi-val">
                      {overviewMetrics.baselineDoc != null
                        ? `${overviewMetrics.baselineDoc} 天`
                        : (report?.safety_metrics?.days_of_supply != null ? `${report.safety_metrics.days_of_supply} 天` : "待拉取")}
                    </span>
                    <span className={`scale-kpi-sub ${overviewMetrics.baselineDoc == null && report?.safety_metrics?.days_of_supply == null ? "warning" : ((overviewMetrics.baselineDoc ?? report?.safety_metrics?.days_of_supply ?? 0) >= 20 ? "positive" : "warning")}`}>
                      {overviewMetrics.baselineDoc == null && report?.safety_metrics?.days_of_supply == null
                        ? "库存快照尚未返回，系统将自动重试"
                        : `底线 12 天 · ${(overviewMetrics.baselineDoc ?? report?.safety_metrics?.days_of_supply ?? 0) >= 20 ? "供应健康" : "库存偏紧"}`}
                    </span>
                  </div>
                </div>

                {/* 30-Day Scale Lifecycle Trajectory Chart */}
                <ScaleTrajectoryChart
                  goal={activeGoal}
                  report={report}
                  context={context}
                  currencySymbol={currSym}
                  interventionDate={interventionDate || activeGoal?.intervention_date}
                />

                {/* Daily Ad Spend, Sales & ACOS Trends Card */}
                <DailyAdPerformanceCard
                  scopeId={activeScope?.scope_id}
                  context={context}
                  report={report}
                  goal={activeGoal}
                  currencySymbol={currSym}
                  marketplace={activeScope?.marketplace}
                  asin={activeScope?.child_asin}
                  interventionDate={interventionDate || activeGoal?.intervention_date}
                />

                {/* Keyword Ranking Trajectory Card (Interactive Chart & Visual Matrix Table) */}
                <KeywordRankingTrajectoryCard
                  targetKeywords={
                    activeGoal?.target_keywords?.length
                      ? activeGoal.target_keywords
                      : (sop?.goal_spec?.target_keywords || [])
                  }
                  rankingMetrics={report?.ranking_metrics}
                  daysSinceIntervention={currentMilestoneInfo.daysElapsed}
                  totalHorizonDays={activeGoal?.schedule_policy?.baseline_horizon_days ?? 21}
                  currencySymbol={currSym}
                  startDate={interventionDate || activeGoal?.intervention_date}
                  interventionDate={interventionDate || activeGoal?.intervention_date}
                />
              </div>
            )}

            {/* TAB 2: 定时任务 (Schedules) */}
            {detailSubTab === "schedules" && (
              activeScope && renderSchedules ? (
                renderSchedules(activeScope, currentScopeSchedules)
              ) : (
                <section className="panel table-panel schedule-table-panel" style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", overflow: "hidden", boxShadow: "var(--shadow-xs)" }}>
                  <div className="section-title" style={{ minHeight: "58px", height: "auto", padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--line)", background: "var(--surface-subtle)" }}>
                    <div>
                      <h2 style={{ margin: 0, fontSize: "14px", fontWeight: 750, color: "var(--ink-heading)" }}>
                        ASIN 定时执行任务 ({currentScopeSchedules.length})
                      </h2>
                      <p style={{ margin: "4px 0 0", fontSize: "12px", color: "var(--muted)" }}>
                        挂载到当前 ASIN 的周期性优化与复盘调度计划，支持每周优化、月度优化与实验复盘。
                      </p>
                    </div>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table className="scale-rank-table">
                      <thead>
                        <tr>
                          <th>任务 ID</th>
                          <th>任务类型</th>
                          <th>运行模式</th>
                          <th>频率 / 本地时间</th>
                          <th>下次运行时间</th>
                          <th>状态</th>
                          <th>操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentScopeSchedules.length > 0 ? (
                          currentScopeSchedules.map((sc) => (
                            <tr key={sc.schedule_id}>
                              <td><code className="mono-badge">{shortId(sc.schedule_id)}</code></td>
                              <td><strong>{taskLabel(sc.task_type)}</strong></td>
                              <td>
                                <span className="scale-rank-badge stable" style={{ fontSize: "11px" }}>
                                  {sc.task_mode === "AUTO_EXECUTE" ? "🚀 自动执行" : "🛡️ 需审批"}
                                </span>
                              </td>
                              <td>{sc.frequency} · {sc.local_time}</td>
                              <td>{dateTime(sc.next_run_at)}</td>
                              <td>
                                <span className={`status ${sc.enabled ? "status-executed" : "status-paused"}`}>
                                  {sc.enabled ? "已启用" : "已暂停"}
                                </span>
                              </td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => handleRunScheduleNow(sc.schedule_id)}
                                  className="secondary-button"
                                  style={{ padding: "3px 8px", fontSize: "11px" }}
                                >
                                  立即运行
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={7} style={{ textAlign: "center", padding: "32px", color: "var(--muted)" }}>
                              尚未为当前 ASIN 创建定时任务。
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              )
            )}

            {/* TAB 3: Runs 历史 (Runs History) */}
            {detailSubTab === "runs" && (
              <section className="panel" style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", overflow: "hidden", boxShadow: "var(--shadow-xs)" }}>
                <div className="section-title" style={{ minHeight: "58px", height: "auto", padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", borderBottom: "1px solid var(--line)", background: "var(--surface-subtle)" }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: "14px", fontWeight: 750, color: "var(--ink-heading)" }}>
                      ASIN 优化与运行历史 ({currentScopeRuns.length})
                    </h2>
                    <p style={{ margin: "4px 0 0", fontSize: "12px", color: "var(--muted)" }}>
                      记录该 ASIN 历次自动调优分析、出价跃迁、加否执行及审批流水。
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={evaluating || !activeGoal}
                    onClick={handleEvaluate}
                    className="primary-button"
                    style={{ padding: "6px 14px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "6px" }}
                  >
                    <Play size={13} /> 立即运行优化
                  </button>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table className="scale-rank-table">
                    <thead>
                      <tr>
                        <th>Run ID</th>
                        <th>任务类型</th>
                        <th>触发方式</th>
                        <th>策略 Hash</th>
                        <th>质量状态</th>
                        <th>运行状态</th>
                        <th>创建时间</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentScopeRuns.length > 0 ? (
                        currentScopeRuns.map((r) => (
                          <tr key={r.run_id}>
                            <td>
                              <button
                                type="button"
                                onClick={() => handleOpenRun(r.run_id)}
                                style={{ background: "none", border: "none", color: "var(--teal)", cursor: "pointer", textDecoration: "underline", fontFamily: "var(--font-mono)", fontWeight: 700 }}
                                title="查看 Run 详情"
                              >
                                {shortId(r.run_id)}
                              </button>
                            </td>
                            <td><strong>{taskLabel(r.task_type)}</strong></td>
                            <td><span className="scale-milestone-phase-badge">{r.trigger_source}</span></td>
                            <td><code className="mono">{shortHash(r.strategy_hash)}</code></td>
                            <td>{r.quality_status ? <span className="status status-complete">{r.quality_status}</span> : "-"}</td>
                            <td>
                              <span className={`status status-${r.state.toLowerCase().replaceAll("_", "-")}`}>
                                {r.state}
                              </span>
                            </td>
                            <td>{dateTime(r.created_at)}</td>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                {r.state === "APPROVAL_REQUIRED" ? (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenRun(r.run_id)}
                                    className="scale-rank-badge falling"
                                    style={{ cursor: "pointer", fontSize: "11px", padding: "2px 8px", border: "none" }}
                                  >
                                    去审批
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenRun(r.run_id)}
                                    className="secondary-button"
                                    style={{ padding: "2px 8px", fontSize: "11px" }}
                                  >
                                    查看详情
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} style={{ textAlign: "center", padding: "30px", color: "var(--muted)" }}>
                            暂无该 ASIN 的历史 Run 记录。可点击右上角「立即运行优化」或「立即自适应诊断」发起。
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Supplementary Compatibility Anchor */}
            <div style={{ display: "none" }}>
              <span>全盘综合 TACOS</span>
              <span>全盘托管 ASIN 战役矩阵</span>
            </div>
          </div>
        ) : (
          /* ========================================================================= */
          /* ========================================================================= */
          /* 正在执行自动驾驶的 ASIN 战役大盘 (横向一长条列表) */
          /* ========================================================================= */
          <div className="scale-view-section">
            {/* Section: 正在执行自动驾驶的 ASIN 战役大盘 */}
            <div className="strategy-section">
              <div className="strategy-section-header">
                <div className="strategy-section-icon" style={{ background: "rgba(13, 148, 136, 0.12)", color: "var(--teal)" }}>
                  <Rocket size={18} />
                </div>
                <div className="scale-fleet-header-toolbar">
                  <div className="scale-fleet-header-info">
                    <div className="scale-fleet-header-title-row">
                      <h3 className="scale-fleet-header-title">
                        正在执行自动驾驶的 ASIN 战役大盘 ({executingScopes.length})
                        <span className="scale-rank-badge rising" style={{ fontSize: "11px", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                          <span className="scale-fleet-pulse-dot" />
                          实时运行中
                        </span>
                      </h3>
                    </div>
                    <p className="scale-fleet-header-subtitle">
                      当前全盘正在执行自动驾驶的 ASIN 列表。点击横条可查看专属详细指标、生命周期进度与关键词跃迁轨迹。
                    </p>
                  </div>

                  {/* Filter Segmented Tabs & Action Buttons */}
                  <div className="scale-fleet-actions-group">
                    <div className="scale-fleet-segmented-tabs" role="tablist">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={fleetFilter === "executing"}
                        className={`scale-fleet-tab ${fleetFilter === "executing" ? "active" : ""}`}
                        onClick={() => setFleetFilter("executing")}
                      >
                        <span className="scale-fleet-tab-label">执行中</span>
                        <span className="scale-fleet-tab-count">{executingScopes.length}</span>
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={fleetFilter === "auto"}
                        className={`scale-fleet-tab ${fleetFilter === "auto" ? "active" : ""}`}
                        onClick={() => setFleetFilter("auto")}
                      >
                        <span className="scale-fleet-tab-label">全自动</span>
                        <span className="scale-fleet-tab-count">{summaryData?.auto_mode_count ?? 0}</span>
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={fleetFilter === "approval"}
                        className={`scale-fleet-tab ${fleetFilter === "approval" ? "active" : ""}`}
                        onClick={() => setFleetFilter("approval")}
                      >
                        <span className="scale-fleet-tab-label">人工审批</span>
                        <span className="scale-fleet-tab-count">{summaryData?.approval_mode_count ?? 0}</span>
                      </button>
                      {(summaryData?.total_pending_approvals ?? 0) > 0 && (
                        <button
                          type="button"
                          role="tab"
                          aria-selected={fleetFilter === "pending"}
                          className={`scale-fleet-tab ${fleetFilter === "pending" ? "active" : ""}`}
                          onClick={() => onNavigateView?.("scale_decisions")}
                        >
                          <span className="scale-fleet-tab-label">待审批</span>
                          <span className="scale-fleet-tab-count urgent">{summaryData?.total_pending_approvals ?? 0}</span>
                        </button>
                      )}
                      <button
                        type="button"
                        role="tab"
                        aria-selected={fleetFilter === "all"}
                        className={`scale-fleet-tab ${fleetFilter === "all" ? "active" : ""}`}
                        onClick={() => setFleetFilter("all")}
                      >
                        <span className="scale-fleet-tab-label">全部托管</span>
                        <span className="scale-fleet-tab-count">{scopes.length}</span>
                      </button>
                    </div>

                    <div style={{ width: "1px", height: "18px", background: "var(--line)", margin: "0 4px" }} />

                    {/* Secondary Action: Adaptive Evaluation */}
                    <button
                      type="button"
                      disabled={evaluating || (summaryData?.active_goals_count ?? 0) === 0}
                      onClick={handleEvaluateAll}
                      className="secondary-button scale-fleet-eval-btn"
                      style={{ padding: "6px 12px", fontSize: "11.5px", display: "inline-flex", alignItems: "center", gap: "5px" }}
                      title="一键对战役大盘所有执行中 ASIN 触发自适应诊断与单站指标隔离校准"
                    >
                      <RefreshCw size={12} className={evaluating ? "spin" : ""} />
                      {evaluating ? "全盘诊断中..." : "立即自适应诊断"}
                    </button>

                    {/* Primary Global Action: Launch / Onboard New ASIN */}
                    <button
                      type="button"
                      onClick={() => onNavigateView?.("scale_launch")}
                      className="primary-button scale-fleet-launch-cta-btn"
                      style={{ padding: "6px 14px", fontSize: "12px", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: "6px" }}
                    >
                      <Rocket size={13} /> 纳管新 ASIN
                    </button>
                  </div>
                </div>
              </div>

              <div className="strategy-section-body" style={{ padding: "16px" }}>
                {displayedFleetScopes.length === 0 ? (
                  <div
                    style={{
                      padding: "40px 20px",
                      textAlign: "center",
                      color: "var(--muted)",
                      background: "var(--surface-subtle)",
                      borderRadius: "var(--radius-md)",
                      border: "1px dashed var(--line-strong)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "10px"
                    }}
                  >
                    <Rocket size={32} style={{ color: "var(--muted-light)", opacity: 0.7 }} />
                    <div style={{ fontSize: "13.5px", fontWeight: 700, color: "var(--ink-heading)" }}>
                      当前筛选条件下无正在执行的 ASIN
                    </div>
                    <div style={{ fontSize: "12px", color: "var(--muted)" }}>
                      您可以切换上方标签查看所有托管 ASIN，或点击右上角按钮纳管新 ASIN。
                    </div>
                    <button
                      type="button"
                      onClick={() => setFleetFilter("all")}
                      className="secondary-button"
                      style={{ marginTop: "6px", fontSize: "12px", padding: "5px 14px" }}
                    >
                      查看全部托管 ASIN ({scopes.length})
                    </button>
                  </div>
                ) : (
                  /* Vertical list of Horizontal Full-Width Strips with 5-Column Grid */
                  <div className="scale-fleet-strip-list">
                    {displayedFleetScopes.map((s) => {
                      const isCurrent = s.scope_id === selectedScopeId;
                      const goalItem = summaryData?.active_goals.find((g) => g.scope_id === s.scope_id);
                      const hasGoal = Boolean(goalItem || (isCurrent && activeGoal));
                      const mode = goalItem?.operating_mode || (isCurrent && activeGoal ? activeGoal.operating_mode : null);
                      const intent = goalItem?.intent || (isCurrent && activeGoal ? activeGoal.intent : null);
                      const intentInfo = getIntentInfo(intent);
                      const pendingCount = goalItem?.pending_approvals_count ?? 0;
                      const tacos = goalItem?.current_tacos !== undefined && goalItem.current_tacos !== null
                        ? `${(goalItem.current_tacos * 100).toFixed(1)}%`
                        : (isCurrent && report ? `${(report.economics_metrics.current_tacos * 100).toFixed(1)}%` : "--%");
                      const budget = goalItem?.max_daily_budget || (isCurrent && activeGoal?.hard_guardrails?.max_daily_ad_spend);
                      const kwsCount = goalItem?.target_keywords_count || (isCurrent && activeGoal?.target_keywords?.length);

                      // Highest Priority State Border
                      const priorityBorderClass = !hasGoal
                        ? "border-priority-idle"
                        : pendingCount > 0
                          ? "border-priority-urgent"
                          : mode === "APPROVAL_REQUIRED"
                            ? "border-priority-approval"
                            : "border-priority-auto";

                      return (
                        <div
                          key={s.scope_id}
                          onClick={() => {
                            onSelectScope(s.scope_id);
                            setViewDetailScopeId(s.scope_id);
                          }}
                          className={`scale-fleet-strip ${isCurrent ? "selected" : ""} ${priorityBorderClass}`}
                        >
                          {/* Column 1: Identity (20%~22%) */}
                          <div className="scale-fleet-strip-col scale-fleet-identity-col">
                            <MarketplaceFlag marketplace={s.marketplace} />
                            <div className="scale-fleet-identity-content">
                              <div className="scale-fleet-asin-title-row">
                                <span className="scale-fleet-mp-badge">{s.marketplace}</span>
                                <strong className="scale-fleet-asin-code">{s.child_asin}</strong>
                                <button
                                  type="button"
                                  className={`scale-fleet-copy-btn ${copiedAsin === s.child_asin ? "copied" : ""}`}
                                  onClick={(e) => handleCopyAsin(e, s.child_asin)}
                                  title={copiedAsin === s.child_asin ? "已复制到剪贴板" : "复制 ASIN"}
                                  aria-label={`复制 ASIN ${s.child_asin}`}
                                >
                                  {copiedAsin === s.child_asin ? <Check size={12} /> : <Copy size={12} />}
                                  {copiedAsin === s.child_asin && <span className="scale-fleet-copy-toast">已复制</span>}
                                </button>
                                {intentInfo && (
                                  <span className={`scale-intent-pill ${intentInfo.badgeClass}`} title={`放量模式：${intentInfo.title} · ${intentInfo.desc}`}>
                                    <span>{intentInfo.icon}</span> {intentInfo.shortTitle}
                                  </span>
                                )}
                              </div>
                              <div className="scale-fleet-sku-row">
                                <span>SKU: {s.sku || "默认商品"}</span>
                                {s.brand && (
                                  <>
                                    <span className="scale-fleet-sku-dot">·</span>
                                    <span className="scale-fleet-brand-name">{s.brand}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Column 2: Key Metrics (25%) - Whitespace-driven, No Gray Box! */}
                          <div className="scale-fleet-strip-col scale-fleet-metrics-group">
                            <div className="scale-fleet-metric-item">
                              <span className="scale-fleet-metric-label">点火期 TACOS</span>
                              <strong className="scale-fleet-metric-value">{tacos}</strong>
                              {goalItem?.current_tacos_window_start && (
                                <span style={{ display: "block", marginTop: "2px", fontSize: "10px", color: "var(--muted)" }}>点火起 {goalItem.current_tacos_window_start}</span>
                              )}
                            </div>
                            <div className="scale-fleet-metric-item">
                              <span className="scale-fleet-metric-label">日预算上限</span>
                              <strong className="scale-fleet-metric-value">
                                {budget ? (
                                  <>
                                    {getMarketplaceCurrencySymbol(s.marketplace)}{budget}
                                    <span className="scale-fleet-metric-unit">/天</span>
                                  </>
                                ) : (
                                  <span style={{ color: "var(--muted-light)" }}>--</span>
                                )}
                              </strong>
                            </div>
                            <div className="scale-fleet-metric-item">
                              <span className="scale-fleet-metric-label">攻坚核心词</span>
                              <strong className="scale-fleet-metric-value accent">
                                {kwsCount ? (
                                  <>
                                    {kwsCount}
                                    <span className="scale-fleet-metric-unit"> 词</span>
                                  </>
                                ) : (
                                  <span style={{ color: "var(--muted-light)" }}>--</span>
                                )}
                              </strong>
                            </div>
                          </div>

                          {/* Column 3: Managed Mode & Action (18%~20%) - Action follows State! */}
                          <div className="scale-fleet-strip-col scale-fleet-mode-col">
                            {hasGoal && mode ? (
                              <div className="scale-fleet-mode-cell">
                                <span className={`scale-fleet-mode-badge ${mode === "AUTO" ? "auto" : "approval"}`}>
                                  <span className={`scale-fleet-status-dot ${mode === "AUTO" ? "auto" : "warning"}`} />
                                  {mode === "AUTO" ? "全自动模式" : "人工审批模式"}
                                </span>
                                <button
                                  type="button"
                                  disabled={togglingScopeId === s.scope_id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleToggleMode(s.scope_id, mode);
                                  }}
                                  className="scale-fleet-sublink-btn"
                                  title={mode === "AUTO" ? "切换为人工审批模式" : "切换为全自动驾驶模式"}
                                  aria-label={`将 ASIN ${s.child_asin} 切换为${mode === "AUTO" ? "人工审批" : "全自动"}`}
                                >
                                  {togglingScopeId === s.scope_id && (
                                    <RefreshCw size={10} className="spin" />
                                  )}
                                  <span>{mode === "AUTO" ? "切换为人工审批" : "切换为全自动"}</span>
                                </button>
                              </div>
                            ) : (
                              <div className="scale-fleet-mode-cell">
                                <span className="scale-fleet-mode-badge idle" style={{ opacity: 0.6, background: "var(--surface-subtle)", border: "1px solid var(--line)" }}>
                                  <span className="scale-fleet-status-dot" style={{ background: "var(--muted-light)" }} />
                                  未启动自动驾驶
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Column 4: Running Health & Pending Tasks (15%) - State Exclusivity */}
                          <div className="scale-fleet-strip-col scale-fleet-health-col">
                            <div className="scale-fleet-health-cell">
                              {!hasGoal ? (
                                <span className="scale-fleet-health-clean" style={{ color: "var(--muted)" }}>
                                  待纳管
                                </span>
                              ) : pendingCount > 0 ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSelectScope(s.scope_id);
                                    onNavigateView?.("scale_decisions");
                                  }}
                                  className="scale-fleet-pending-pill urgent"
                                  title={`待审批阻断：共有 ${pendingCount} 项自动化调整建议待人工确认，点击直达审批中心`}
                                  aria-label={`待审批阻断：共有 ${pendingCount} 项自动化调整建议待人工确认`}
                                >
                                  <AlertTriangle size={12} /> {pendingCount} 条待审批
                                </button>
                              ) : (
                                <>
                                  <span className="scale-fleet-health-clean">
                                    <CheckCircle2 size={13} /> 无待办
                                  </span>
                                  <div className="scale-fleet-verdict-row">
                                    <Check size={12} className="scale-fleet-verdict-icon" />
                                    <span className="scale-fleet-verdict-text">
                                      {goalItem?.verdict ? (
                                        goalItem.verdict === "PROCEED_TO_NEXT" ? "加速冲刺"
                                        : goalItem.verdict === "STABILIZE_DEFENSE" ? "登顶固化"
                                        : goalItem.verdict === "PIVOT_STRATEGY" ? "策略校准"
                                        : "稳健推进"
                                      ) : "稳健推进"}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Column 5: Row Action (18%~20% - Right Aligned) */}
                          <div className="scale-fleet-strip-col scale-fleet-actions-col">
                            {hasGoal ? (
                              <div className="scale-fleet-action-buttons">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSelectScope(s.scope_id);
                                    setViewDetailScopeId(s.scope_id);
                                  }}
                                  className="scale-fleet-view-detail-btn primary-button"
                                  title="点击查看专属详细指标、生命周期进度与关键词跃迁轨迹"
                                >
                                  <span>查看详细信息 ➔</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setScopeToRemove(s);
                                  }}
                                  className="scale-fleet-remove-btn"
                                  disabled={removingScopeId === s.scope_id}
                                  title="停止自动驾驶（保留日常系统与历史记录）"
                                  aria-label={`停止 ${s.child_asin} 的自动驾驶`}
                                >
                                  {removingScopeId === s.scope_id ? (
                                    <RefreshCw size={14} className="spin" />
                                  ) : (
                                    <Trash2 size={14} />
                                  )}
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSelectScope(s.scope_id);
                                  setLaunchScopeId(s.scope_id);
                                  onNavigateView?.("scale_launch");
                                }}
                                className="scale-fleet-view-detail-btn secondary-button"
                                style={{ background: "var(--teal-subtle)", color: "var(--teal)", borderColor: "var(--teal-border)" }}
                                title="为该 ASIN 发起放量自动驾驶"
                              >
                                <span>发起放量 ➔</span>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Supplementary Compatibility Anchor: 全盘托管 ASIN 战役矩阵 */}
            <div style={{ display: "none" }}>
              <span>全盘托管 ASIN 战役矩阵</span>
            </div>
          </div>
        )
      )}

      {/* ========================================================================= */}
      {/* VIEW 2: 放量优化运行记录 (scale_optimizations) - 自动驾驶 Runs 流水 */}
      {/* ========================================================================= */}
      {/* ========================================================================= */}
      {/* VIEW 3: 放量优化运行记录 (scale_optimizations) - 自动驾驶 Runs 流水 */}
      {/* ========================================================================= */}
      {activeSubView === "optimizations" && (
        <div className="scale-view-section" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* 1. Top 4 KPI Summary Metric Cards */}
          <div className="runs-metrics-grid">
            <div
              className={`runs-metric-card ${runTab === "all" ? "active" : ""}`}
              onClick={() => { setRunTab("all"); setRunPage(1); }}
              title="点击查看全部自动驾驶运行记录"
            >
              <div className="runs-metric-header">
                <span>今日运行总数</span>
                <Activity size={16} />
              </div>
              <div className="runs-metric-value">{metrics.todayCount}</div>
              <div className="runs-metric-sub">历史累计 {autonomousRuns.length} 次放量运行</div>
            </div>

            <div
              className={`runs-metric-card warning ${runTab === "approval" ? "active" : ""}`}
              onClick={() => { setRunTab("approval"); setRunPage(1); }}
              title="点击查看待人工审批任务"
            >
              <div className="runs-metric-header">
                <span>待人工审批</span>
                <ShieldAlert size={16} />
              </div>
              <div className="runs-metric-value">{metrics.pendingApprovalCount}</div>
              <div className="runs-metric-sub">需运营或审批人确认执行</div>
            </div>

            <div
              className={`runs-metric-card success ${runTab === "completed" ? "active" : ""}`}
              onClick={() => { setRunTab("completed"); setRunPage(1); }}
              title="点击查看已执行完成任务"
            >
              <div className="runs-metric-header">
                <span>已成功执行</span>
                <CheckCircle2 size={16} />
              </div>
              <div className="runs-metric-value">{metrics.executedCount}</div>
              <div className="runs-metric-sub">实际下发广告调整与写回</div>
            </div>

            <div
              className={`runs-metric-card danger ${runTab === "issues" ? "active" : ""}`}
              onClick={() => { setRunTab("issues"); setRunPage(1); }}
              title="点击查看异常阻断任务"
            >
              <div className="runs-metric-header">
                <span>异常 / 阻断告警</span>
                <AlertTriangle size={16} />
              </div>
              <div className="runs-metric-value">{metrics.issuesCount}</div>
              <div className="runs-metric-sub">包含失败、熔断与已拒绝记录</div>
            </div>
          </div>

          {/* 2. Main Panel with Tabs, Filters, Table and Pagination */}
          <section className="panel table-panel" style={{ padding: "0", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", overflow: "hidden" }}>
            {/* Quick Status Tabs */}
            <div className="runs-tabs-container">
              <div className="runs-tabs-list" role="tablist">
                <button
                  type="button"
                  className={`runs-tab-btn ${runTab === "all" ? "active" : ""}`}
                  onClick={() => { setRunTab("all"); setRunPage(1); }}
                >
                  <span>全部</span>
                  <span className="runs-tab-count">{autonomousRuns.length}</span>
                </button>
                <button
                  type="button"
                  className={`runs-tab-btn ${runTab === "approval" ? "active" : ""}`}
                  onClick={() => { setRunTab("approval"); setRunPage(1); }}
                >
                  <span>🔥 待审批</span>
                  <span className={`runs-tab-count ${metrics.pendingApprovalCount > 0 ? "alert" : ""}`}>
                    {metrics.pendingApprovalCount}
                  </span>
                </button>
                <button
                  type="button"
                  className={`runs-tab-btn ${runTab === "running" ? "active" : ""}`}
                  onClick={() => { setRunTab("running"); setRunPage(1); }}
                >
                  <span>⏳ 进行中</span>
                  <span className="runs-tab-count">{metrics.runningCount}</span>
                </button>
                <button
                  type="button"
                  className={`runs-tab-btn ${runTab === "completed" ? "active" : ""}`}
                  onClick={() => { setRunTab("completed"); setRunPage(1); }}
                >
                  <span>✅ 已完成</span>
                  <span className="runs-tab-count">{metrics.completedCount}</span>
                </button>
                <button
                  type="button"
                  className={`runs-tab-btn ${runTab === "issues" ? "active" : ""}`}
                  onClick={() => { setRunTab("issues"); setRunPage(1); }}
                >
                  <span>⚠️ 异常/阻断</span>
                  <span className="runs-tab-count">{metrics.issuesCount}</span>
                </button>
              </div>
            </div>

            {/* Filter Toolbar */}
            <div className="runs-filter-toolbar">
              <div className="search">
                <Search size={15} />
                <input
                  placeholder="搜索 Run ID、ASIN、状态或 Hash..."
                  value={runQuery}
                  onChange={(e) => { setRunQuery(e.target.value); setRunPage(1); }}
                />
                {runQuery && (
                  <button type="button" className="search-clear-btn" onClick={() => { setRunQuery(""); setRunPage(1); }}>
                    <X size={14} />
                  </button>
                )}
              </div>

              <CustomSelect
                value={runScopeId}
                onChange={(val) => { setRunScopeId(val); setRunPage(1); }}
                options={runSkuOptions}
                title="按 SKU 筛选"
                aria-label="按 SKU 筛选"
              />

              <CustomSelect
                value={runTaskType}
                onChange={(val) => { setRunTaskType(val); setRunPage(1); }}
                options={runTaskTypeOptions}
                title="按任务类型筛选"
                aria-label="按任务类型筛选"
              />

              <CustomSelect
                value={runTrigger}
                onChange={(val) => { setRunTrigger(val); setRunPage(1); }}
                options={runTriggerOptions}
                title="按触发来源筛选"
                aria-label="按触发来源筛选"
              />

              <CustomSelect
                value={runDateFilter}
                onChange={(val) => { setRunDateFilter(val); setRunPage(1); }}
                options={runDateOptions}
                title="按时间范围筛选"
                aria-label="按时间范围筛选"
              />

              {hasActiveFilters && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={resetFilters}
                  style={{ padding: "6px 12px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "4px" }}
                  title="重置所有筛选条件"
                >
                  <RotateCcw size={12} /> 重置筛选
                </button>
              )}

              <button
                type="button"
                onClick={() => void loadRuns()}
                className="secondary-button"
                style={{ padding: "6px 12px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "4px", marginLeft: "auto" }}
                title="刷新 Runs 流水"
              >
                <RefreshCw size={13} className={loadingRuns ? "spin" : ""} /> 刷新
              </button>
            </div>

            {/* Runs Table */}
            <div style={{ overflowX: "auto" }}>
              <table className="scale-rank-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>RUN ID</th>
                    <th>SCOPE</th>
                    <th>任务类型</th>
                    <th>运行模式</th>
                    <th>触发来源</th>
                    <th>质量状态</th>
                    <th>策略 HASH</th>
                    <th>运行状态</th>
                    <th>创建时间</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.length > 0 ? (
                    pageItems.map((r) => {
                      const targetScope = scopes.find((s) => s.scope_id === r.scope_id);
                      return (
                        <tr key={r.run_id}>
                          <td>
                            <button
                              type="button"
                              onClick={() => handleOpenRun(r.run_id)}
                              style={{
                                background: "none",
                                border: "none",
                                color: "var(--teal)",
                                cursor: "pointer",
                                textDecoration: "underline",
                                fontFamily: "var(--font-mono)",
                                fontWeight: 700
                              }}
                              title="查看 Run 详情与分析报告"
                            >
                              {shortId(r.run_id)}
                            </button>
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              {targetScope ? (
                                <>
                                  <MarketplaceFlag marketplace={targetScope.marketplace} />
                                  <span style={{ fontWeight: 600 }}>{targetScope.sku || targetScope.child_asin || "SKU"}</span>
                                </>
                              ) : (
                                <span style={{ color: "var(--muted)", fontSize: "11px" }}>{shortId(r.scope_id)}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <strong style={{ fontSize: "12.5px" }}>{taskLabel(r.task_type)}</strong>
                          </td>
                          <td>
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: 700,
                                padding: "2px 6px",
                                borderRadius: "4px",
                                background: r.task_mode === "AUTO_EXECUTE" ? "rgba(34, 197, 94, 0.12)" : "rgba(168, 85, 247, 0.12)",
                                color: r.task_mode === "AUTO_EXECUTE" ? "var(--status-success-ink)" : "#a855f7"
                              }}
                            >
                              {r.task_mode === "AUTO_EXECUTE" ? "🚀 全自动" : "🛡️ 人工审批"}
                            </span>
                          </td>
                          <td>
                            <span className="scale-milestone-phase-badge">{r.trigger_source}</span>
                          </td>
                          <td>
                            {r.quality_status ? (
                              <span className={`status ${r.quality_status === "COMPLETE" ? "status-complete" : "status-warning"}`}>
                                {r.quality_status}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            <code className="mono">{shortHash(r.strategy_hash)}</code>
                          </td>
                          <td>
                            <span className={`status status-${r.state.toLowerCase().replaceAll("_", "-")}`}>
                              {r.state}
                            </span>
                          </td>
                          <td style={{ fontSize: "12px", color: "var(--muted)" }}>
                            {dateTime(r.created_at)}
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              {r.state === "APPROVAL_REQUIRED" ? (
                                <button
                                  type="button"
                                  onClick={() => handleOpenRun(r.run_id)}
                                  className="scale-rank-badge falling"
                                  style={{ cursor: "pointer", fontSize: "11px", padding: "3px 8px", border: "none" }}
                                >
                                  去审批
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleOpenRun(r.run_id)}
                                  className="secondary-button"
                                  style={{ padding: "3px 8px", fontSize: "11px" }}
                                >
                                  查看详情
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={10} style={{ textAlign: "center", padding: "36px", color: "var(--muted)" }}>
                        暂无符合条件的自动驾驶 Runs 运行记录。可点击右上角「立即运行优化」触发。
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalFiltered > 0 && (
              <div className="pagination-bar" style={{ padding: "12px 16px", borderTop: "1px solid var(--line)", background: "var(--surface)" }}>
                <div className="pagination-info">
                  <span>
                    显示第 <strong>{(currentPage - 1) * runPageSize + 1}</strong> - <strong>{Math.min(currentPage * runPageSize, totalFiltered)}</strong> 条，共 <strong>{totalFiltered}</strong> 条记录
                  </span>
                  <div className="pagination-size-select">
                    <span>每页</span>
                    <select
                      value={runPageSize}
                      onChange={(e) => { setRunPageSize(Number(e.target.value)); setRunPage(1); }}
                    >
                      {[10, 20, 50].map((size) => (
                        <option key={size} value={size}>
                          {size} 条
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="pagination-controls">
                  <button
                    type="button"
                    className="icon-button pagination-btn"
                    disabled={currentPage <= 1}
                    onClick={() => setRunPage(currentPage - 1)}
                    title="上一页"
                    aria-label="上一页"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="pagination-current">
                    第 <strong>{currentPage}</strong> / {totalPages} 页
                  </span>
                  <button
                    type="button"
                    className="icon-button pagination-btn"
                    disabled={currentPage >= totalPages}
                    onClick={() => setRunPage(currentPage + 1)}
                    title="下一页"
                    aria-label="下一页"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {/* ========================================================================= */}
      {/* VIEW 4: 放量审批中心 (scale_decisions) - 自动驾驶专属待审批调优队列 */}
      {/* ========================================================================= */}
      {activeSubView === "decisions" && (
        <div className="scale-view-section" style={{ padding: 0 }}>
          {pendingApprovalsList.length === 0 ? (
            <section className="empty-state" style={{ background: "var(--surface)", border: "1px dashed var(--line)", borderRadius: "var(--radius-lg)", padding: "48px 24px", textAlign: "center" }}>
              <div style={{ width: "54px", height: "54px", borderRadius: "50%", background: "rgba(34, 197, 94, 0.12)", color: "var(--status-success-ink)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <CheckCircle2 size={28} />
              </div>
              <h2 style={{ fontSize: "16px", fontWeight: 750, color: "var(--ink-heading)", margin: "0 0 6px" }}>没有待审批的放量调优任务</h2>
              <p style={{ fontSize: "12.5px", color: "var(--muted)", maxWidth: "480px", margin: "0 auto" }}>
                当前所有 ASIN 均在全自动驾驶推进中，或处于“人工审批”模式下生成的战役调优建议均已处理完毕。
              </p>
            </section>
          ) : (
            <>
              {/* 1. Global Impact & Risk KPI Dashboard */}
              <div className="approval-kpi-grid">
                <div
                  className={`approval-kpi-card ${approvalQuickTab === "ALL" ? "is-active" : ""}`}
                  onClick={() => setApprovalQuickTab("ALL")}
                >
                  <div className="approval-kpi-header">
                    <span className="approval-kpi-icon total"><Layers size={15} /></span>
                    <span className="approval-kpi-title">待审建议总览</span>
                  </div>
                  <div className="approval-kpi-body">
                    <strong className="approval-kpi-value">{approvalKpiStats.totalProposedActions}</strong>
                    <span className="approval-kpi-unit">项动作</span>
                  </div>
                  <div className="approval-kpi-sub">
                    <span>覆盖 <strong>{approvalKpiStats.totalBatches}</strong> 个放量 SKU</span>
                  </div>
                </div>

                <div
                  className={`approval-kpi-card ${approvalQuickTab === "LOW_RISK" ? "is-active" : ""}`}
                  onClick={() => setApprovalQuickTab(approvalQuickTab === "LOW_RISK" ? "ALL" : "LOW_RISK")}
                >
                  <div className="approval-kpi-header">
                    <span className="approval-kpi-icon low"><ShieldCheck size={15} /></span>
                    <span className="approval-kpi-title">低风险微调 (安全)</span>
                  </div>
                  <div className="approval-kpi-body">
                    <strong className="approval-kpi-value text-emerald">{approvalKpiStats.lowRiskCount}</strong>
                    <span className="approval-kpi-unit">项</span>
                  </div>
                  <div className="approval-kpi-sub">
                    <span>中高风险: <strong>{approvalKpiStats.mediumRiskCount + approvalKpiStats.highRiskCount}</strong> 项</span>
                  </div>
                </div>

                <div
                  className={`approval-kpi-card ${approvalQuickTab === "BID_DOWN" ? "is-active" : ""}`}
                  onClick={() => setApprovalQuickTab(approvalQuickTab === "BID_DOWN" ? "ALL" : "BID_DOWN")}
                >
                  <div className="approval-kpi-header">
                    <span className="approval-kpi-icon down"><TrendingDown size={15} /></span>
                    <span className="approval-kpi-title">出价调降 (控本)</span>
                  </div>
                  <div className="approval-kpi-body">
                    <strong className="approval-kpi-value text-teal">{approvalKpiStats.bidDownCount}</strong>
                    <span className="approval-kpi-unit">项</span>
                  </div>
                  <div className="approval-kpi-sub">
                    <span>抑制低效流量跑冒</span>
                  </div>
                </div>

                <div
                  className={`approval-kpi-card ${approvalQuickTab === "BID_UP" ? "is-active" : ""}`}
                  onClick={() => setApprovalQuickTab(approvalQuickTab === "BID_UP" ? "ALL" : "BID_UP")}
                >
                  <div className="approval-kpi-header">
                    <span className="approval-kpi-icon total" style={{ color: "var(--teal)" }}><TrendingUp size={15} /></span>
                    <span className="approval-kpi-title">出价调升 (放量)</span>
                  </div>
                  <div className="approval-kpi-body">
                    <strong className="approval-kpi-value" style={{ color: "var(--teal)" }}>{approvalKpiStats.bidUpCount}</strong>
                    <span className="approval-kpi-unit">项</span>
                  </div>
                  <div className="approval-kpi-sub">
                    <span>核心词冲刺卡位</span>
                  </div>
                </div>

                <div
                  className={`approval-kpi-card ${approvalQuickTab === "NEGATIVE" ? "is-active" : ""}`}
                  onClick={() => setApprovalQuickTab(approvalQuickTab === "NEGATIVE" ? "ALL" : "NEGATIVE")}
                >
                  <div className="approval-kpi-header">
                    <span className="approval-kpi-icon neg"><XCircle size={15} /></span>
                    <span className="approval-kpi-title">新增否定隔离</span>
                  </div>
                  <div className="approval-kpi-body">
                    <strong className="approval-kpi-value text-rose">{approvalKpiStats.negativeCount}</strong>
                    <span className="approval-kpi-unit">项</span>
                  </div>
                  <div className="approval-kpi-sub">
                    <span>跨战役精准加否隔离</span>
                  </div>
                </div>
              </div>

              {/* 2. Filter Bar & Quick Tabs */}
              <div className="approval-filter-bar">
                <div className="approval-quick-tabs">
                  {[
                    { id: "ALL", label: "全部待审", count: approvalKpiStats.totalProposedActions, icon: <Layers size={13} /> },
                    { id: "LOW_RISK", label: "仅看低风险", count: approvalKpiStats.lowRiskCount, icon: <ShieldCheck size={13} /> },
                    { id: "HIGH_MEDIUM_RISK", label: "含中/高风险", count: approvalKpiStats.mediumRiskCount + approvalKpiStats.highRiskCount, icon: <ShieldAlert size={13} /> },
                    { id: "BID_DOWN", label: "出价调降", count: approvalKpiStats.bidDownCount, icon: <TrendingDown size={13} /> },
                    { id: "BID_UP", label: "出价调升", count: approvalKpiStats.bidUpCount, icon: <TrendingUp size={13} /> },
                    { id: "NEGATIVE", label: "新增否定", count: approvalKpiStats.negativeCount, icon: <XCircle size={13} /> },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`approval-tab-btn ${approvalQuickTab === tab.id ? "is-active" : ""}`}
                      onClick={() => setApprovalQuickTab(tab.id as any)}
                    >
                      <span className="approval-tab-icon">{tab.icon}</span>
                      <span>{tab.label}</span>
                      <span className="approval-tab-count">{tab.count}</span>
                    </button>
                  ))}
                </div>

                <div className="approval-filter-row">
                  <div className="approval-search-wrap">
                    <Search size={14} className="approval-search-icon" />
                    <input
                      type="text"
                      className="approval-search-input"
                      placeholder="搜索核心词、活动名、ASIN、SKU 或优化理由..."
                      value={approvalSearchQuery}
                      onChange={(e) => setApprovalSearchQuery(e.target.value)}
                    />
                    {approvalSearchQuery && (
                      <button type="button" className="approval-search-clear" onClick={() => setApprovalSearchQuery("")}>
                        <X size={13} />
                      </button>
                    )}
                  </div>

                  <div className="approval-filter-select-group">
                    <CustomSelect
                      value={approvalScopeFilter}
                      onChange={(val) => setApprovalScopeFilter(val)}
                      options={approvalScopeOptions}
                      title="按 SKU 筛选"
                      aria-label="按 SKU 筛选"
                      className="approval-mp-select"
                    />

                    <CustomSelect
                      value={approvalRiskFilter}
                      onChange={(val) => setApprovalRiskFilter(val)}
                      options={approvalRiskOptions}
                      title="按风险筛选"
                      aria-label="按风险筛选"
                    />

                    <CustomSelect
                      value={approvalActionTypeFilter}
                      onChange={(val) => setApprovalActionTypeFilter(val)}
                      options={approvalActionTypeOptions}
                      title="按动作类型筛选"
                      aria-label="按动作类型筛选"
                    />

                    {(approvalQuickTab !== "ALL" ||
                      approvalScopeFilter !== "ALL" ||
                      approvalRiskFilter !== "ALL" ||
                      approvalActionTypeFilter !== "ALL" ||
                      approvalSearchQuery.trim() !== "") && (
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => {
                          setApprovalQuickTab("ALL");
                          setApprovalScopeFilter("ALL");
                          setApprovalRiskFilter("ALL");
                          setApprovalActionTypeFilter("ALL");
                          setApprovalSearchQuery("");
                        }}
                        style={{ padding: "6px 12px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "4px" }}
                        title="重置所有筛选条件"
                      >
                        <RotateCcw size={12} /> 重置筛选
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => void loadOperations()}
                      className="secondary-button"
                      style={{ padding: "6px 12px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "4px", marginLeft: "auto" }}
                      title="刷新待审批队列"
                    >
                      <RefreshCw size={13} className={loadingOps ? "spin" : ""} /> 刷新
                    </button>
                  </div>
                </div>
              </div>

              {/* 3. Global Batch Toolbar */}
              <div className="approval-batch-toolbar" style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)", padding: "10px 16px", marginBottom: "14px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => {
                      const lowIds = filteredPendingOps.filter((o) => o.risk_level === "LOW").map((o) => o.operation_id);
                      setSelectedOpIds(new Set(lowIds));
                      setStatusMessage({ text: `已一键勾选当前 ${lowIds.length} 项低风险安全动作`, type: "success" });
                    }}
                    className="secondary-button"
                    style={{ padding: "5px 12px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "5px" }}
                  >
                    <ShieldCheck size={13} style={{ color: "var(--teal)" }} /> 勾选低风险 ({filteredPendingOps.filter((o) => o.risk_level === "LOW").length})
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      const allIds = filteredPendingOps.map((o) => o.operation_id);
                      setSelectedOpIds(new Set(allIds));
                    }}
                    className="secondary-button"
                    style={{ padding: "5px 12px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "5px" }}
                  >
                    <CheckSquare size={13} /> 勾选当前筛选 ({filteredPendingOps.length})
                  </button>

                  {selectedOpIds.size > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedOpIds(new Set())}
                      className="secondary-button"
                      style={{ padding: "5px 10px", fontSize: "12px" }}
                    >
                      清空勾选
                    </button>
                  )}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  {selectedOpIds.size > 0 ? (
                    <>
                      <button
                        type="button"
                        disabled={processingAction}
                        onClick={() => handleApproveOperations(Array.from(selectedOpIds))}
                        className="primary-button"
                        style={{ padding: "6px 16px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "6px" }}
                      >
                        <CheckCircle2 size={13} /> 批量批准所选 ({selectedOpIds.size})
                      </button>
                      <button
                        type="button"
                        disabled={processingAction}
                        onClick={() => handleRejectOperations(Array.from(selectedOpIds))}
                        className="secondary-button"
                        style={{ padding: "6px 12px", fontSize: "12px", color: "var(--red)" }}
                      >
                        驳回所选 ({selectedOpIds.size})
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      disabled={processingAction || filteredPendingOps.length === 0}
                      onClick={() => handleApproveOperations(filteredPendingOps.map((o) => o.operation_id))}
                      className="primary-button"
                      style={{ padding: "6px 16px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "6px" }}
                    >
                      <CheckCircle2 size={13} /> 一键全部通过 ({filteredPendingOps.length})
                    </button>
                  )}
                </div>
              </div>

              {/* 4. Grouped Batch Cards */}
              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                {groupedPendingBatches.map((batch) => {
                  const targetScope = batch.scope;
                  const isBatchExpanded = approvalExpandedBatches[batch.scopeId] !== false; // default expanded

                  return (
                    <div
                      key={batch.scopeId}
                      className="approval-plan-card"
                      style={{
                        background: "var(--surface)",
                        border: "1px solid var(--line)",
                        borderRadius: "var(--radius-lg)",
                        overflow: "hidden",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.03)"
                      }}
                    >
                      {/* Batch Header */}
                      <div
                        style={{
                          padding: "14px 18px",
                          background: "var(--surface-subtle)",
                          borderBottom: isBatchExpanded ? "1px solid var(--line)" : "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: "12px",
                          cursor: "pointer"
                        }}
                        onClick={() => {
                          setApprovalExpandedBatches((prev) => ({
                            ...prev,
                            [batch.scopeId]: !isBatchExpanded
                          }));
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <button
                            type="button"
                            className="icon-button"
                            style={{ padding: "2px", border: "none", background: "none", cursor: "pointer" }}
                          >
                            {isBatchExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                          </button>

                          {targetScope ? (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <MarketplaceFlag marketplace={targetScope.marketplace} />
                              <strong style={{ fontSize: "14px", color: "var(--ink-heading)" }}>
                                {targetScope.marketplace} · {targetScope.sku || targetScope.child_asin}
                              </strong>
                              <span style={{ fontSize: "12px", color: "var(--muted)" }}>
                                ({targetScope.child_asin})
                              </span>
                            </div>
                          ) : (
                            <strong>Scope #{batch.scopeId}</strong>
                          )}

                          <span className="scale-milestone-active-badge" style={{ fontSize: "11px", marginLeft: "6px" }}>
                            🔥 放量战役专属待审批
                          </span>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span
                            className="scale-rank-badge falling"
                            style={{ fontSize: "11px", fontWeight: 700, padding: "3px 8px" }}
                          >
                            🔴 共 {batch.items.length} 项动作待审
                          </span>

                          <button
                            type="button"
                            disabled={processingAction}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleApproveOperations(batch.items.map((i) => i.operation_id));
                            }}
                            className="secondary-button"
                            style={{ padding: "4px 10px", fontSize: "11.5px", display: "inline-flex", alignItems: "center", gap: "4px" }}
                          >
                            <CheckCircle2 size={12} /> 一键批准此 SKU
                          </button>

                          <button
                            type="button"
                            disabled={processingAction}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleRejectOperations(batch.items.map((i) => i.operation_id));
                            }}
                            className="secondary-button"
                            style={{ padding: "4px 10px", fontSize: "11.5px", color: "var(--red)" }}
                          >
                            驳回此 SKU
                          </button>
                        </div>
                      </div>

                      {/* Action items inside batch */}
                      {isBatchExpanded && (
                        <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: "10px" }}>
                          {batch.items.map((op) => {
                            let beforeVal: any = {};
                            let afterVal: any = {};
                            try {
                              beforeVal = JSON.parse(op.before_value_json || "{}");
                              afterVal = JSON.parse(op.after_value_json || "{}");
                            } catch {}

                            const isChecked = selectedOpIds.has(op.operation_id);
                            const currentAdjustedVal = inlineAdjustments[op.operation_id]?.bid ?? afterVal.bid;
                            const isNegative = op.action_type.includes("NEGATIVE");
                            const isBudget = op.action_type.includes("BUDGET");
                            const tosEntries = Array.isArray(afterVal.placement_bidding)
                              ? afterVal.placement_bidding
                              : Array.isArray(afterVal.dynamicBidding?.placementBidding)
                                ? afterVal.dynamicBidding.placementBidding
                                : [];
                            const tosPercentage = afterVal.top_of_search_multiplier_pct
                              ?? tosEntries.find((entry: any) => String(entry?.placement ?? "").toUpperCase() === "PLACEMENT_TOP")?.percentage;
                            const isPlacement = op.action_type === "PLACEMENT_UPDATE" || tosPercentage !== undefined;
                            const fromBid = beforeVal.bid ?? beforeVal.daily_budget;
                            const toBid = afterVal.bid ?? afterVal.daily_budget;

                            return (
                              <div
                                key={op.operation_id}
                                style={{
                                  padding: "12px 14px",
                                  background: isChecked ? "rgba(13, 148, 136, 0.04)" : "var(--surface)",
                                  border: isChecked ? "1.5px solid var(--teal)" : "1px solid var(--line)",
                                  borderRadius: "var(--radius-md)",
                                  display: "flex",
                                  alignItems: "flex-start",
                                  justifyContent: "space-between",
                                  flexWrap: "wrap",
                                  gap: "14px",
                                  transition: "all 0.12s ease"
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", flex: 1, minWidth: "280px" }}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const next = new Set(selectedOpIds);
                                      if (next.has(op.operation_id)) next.delete(op.operation_id);
                                      else next.add(op.operation_id);
                                      setSelectedOpIds(next);
                                    }}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                      color: isChecked ? "var(--teal)" : "var(--muted)",
                                      padding: "2px 0",
                                      marginTop: "2px"
                                    }}
                                  >
                                    {isChecked ? <CheckSquare size={17} /> : <Square size={17} />}
                                  </button>

                                  <div style={{ display: "flex", flexDirection: "column", gap: "5px", flex: 1 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                      <span
                                        className="scale-milestone-phase-badge"
                                        style={{
                                          fontSize: "11px",
                                          background: isNegative ? "rgba(225, 29, 72, 0.1)" : isPlacement ? "rgba(14, 116, 144, 0.1)" : isBudget ? "rgba(168, 85, 247, 0.1)" : "rgba(13, 148, 136, 0.1)",
                                          color: isNegative ? "#e11d48" : isPlacement ? "#0e7490" : isBudget ? "#a855f7" : "var(--teal)",
                                          border: `1px solid ${isNegative ? "rgba(225, 29, 72, 0.2)" : isPlacement ? "rgba(14, 116, 144, 0.2)" : isBudget ? "rgba(168, 85, 247, 0.2)" : "rgba(13, 148, 136, 0.2)"}`
                                        }}
                                      >
                                        {op.action_type}
                                      </span>

                                      <strong style={{ fontSize: "13.5px", color: "var(--ink-heading)" }}>
                                        {op.target_entity_name}
                                      </strong>

                                      {op.campaign_name && (
                                        <span style={{ fontSize: "11.5px", color: "var(--muted)", background: "var(--surface-subtle)", padding: "2px 6px", borderRadius: "4px" }}>
                                          {op.campaign_name}
                                        </span>
                                      )}

                                      <span
                                        style={{
                                          fontSize: "11px",
                                          padding: "1px 6px",
                                          borderRadius: "4px",
                                          fontWeight: 650,
                                          background: op.risk_level === "HIGH" ? "rgba(225, 29, 72, 0.1)" : op.risk_level === "MEDIUM" ? "rgba(245, 158, 11, 0.1)" : "rgba(34, 197, 94, 0.1)",
                                          color: op.risk_level === "HIGH" ? "#e11d48" : op.risk_level === "MEDIUM" ? "#d97706" : "var(--status-success-ink)"
                                        }}
                                      >
                                        {op.risk_level === "HIGH" ? "⚠️ 高风险" : op.risk_level === "MEDIUM" ? "⚡ 中风险" : "🛡️ 低风险"}
                                      </span>
                                    </div>

                                    {/* Diff Comparison */}
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", margin: "2px 0" }}>
                                      {isNegative ? (
                                        <span style={{ color: "#e11d48", fontWeight: 600 }}>
                                          下发 Negative Exact 精准否定隔离
                                        </span>
                                      ) : isPlacement ? (
                                        <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                          <span style={{ color: "var(--muted)" }}>日预算:</span>
                                          <del style={{ color: "var(--muted)" }}>€{beforeVal.daily_budget ?? 0}</del>
                                          <ArrowRight size={11} />
                                          <ins style={{ textDecoration: "none", color: "var(--ink-heading)", fontWeight: 700 }}>
                                            €{afterVal.daily_budget ?? 0} / 日
                                          </ins>
                                          <span style={{ fontSize: "11px", color: "#0e7490", background: "rgba(14, 116, 144, 0.1)", padding: "1px 6px", borderRadius: "3px", fontWeight: 700 }}>
                                            +{tosPercentage}% TOS
                                          </span>
                                        </div>
                                      ) : isBudget ? (
                                        <div style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                                          <span style={{ color: "var(--muted)" }}>日预算:</span>
                                          <del style={{ color: "var(--muted)" }}>€{beforeVal.daily_budget ?? 0}</del>
                                          <ArrowRight size={11} />
                                          <ins style={{ textDecoration: "none", color: "var(--ink-heading)", fontWeight: 700 }}>
                                            €{afterVal.daily_budget ?? 0} / 日
                                          </ins>
                                        </div>
                                      ) : toBid !== undefined || tosPercentage !== undefined ? (
                                        <div style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                                          {toBid !== undefined && <span style={{ color: "var(--muted)" }}>{isBudget ? "预算:" : "出价:"}</span>}
                                          {toBid !== undefined && fromBid !== undefined && (
                                            <>
                                              <del style={{ color: "var(--muted)" }}>€{fromBid}</del>
                                              <ArrowRight size={11} />
                                            </>
                                          )}
                                          {toBid !== undefined && <ins style={{ textDecoration: "none", color: "var(--teal)", fontWeight: 750 }}>
                                            €{toBid}{isBudget ? " / 日" : ""}
                                          </ins>}
                                          {tosPercentage !== undefined && (
                                            <span style={{ fontSize: "11px", color: "var(--teal)", background: "rgba(13, 148, 136, 0.1)", padding: "1px 5px", borderRadius: "3px" }}>
                                              +{tosPercentage}% TOS
                                            </span>
                                          )}
                                        </div>
                                      ) : null}
                                    </div>

                                    {/* Rationale */}
                                    <div style={{ fontSize: "11.5px", color: "var(--muted)", lineHeight: 1.4 }}>
                                      {op.rationale}
                                    </div>
                                  </div>
                                </div>

                                {/* Right: Inline Edit & Single Actions */}
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                  {afterVal.bid !== undefined && (
                                    <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px" }}>
                                      <span style={{ color: "var(--muted)" }}>微调出价:</span>
                                      <input
                                        type="number"
                                        step="0.05"
                                        value={currentAdjustedVal}
                                        onChange={(e) => {
                                          setInlineAdjustments({
                                            ...inlineAdjustments,
                                            [op.operation_id]: { ...afterVal, bid: Number(e.target.value) }
                                          });
                                        }}
                                        style={{ width: "72px", padding: "4px 6px", fontSize: "12px", borderRadius: "4px", border: "1px solid var(--line)" }}
                                      />
                                    </div>
                                  )}

                                  <button
                                    type="button"
                                    disabled={processingAction}
                                    onClick={() => handleApproveOperations([op.operation_id])}
                                    className="primary-button"
                                    style={{ padding: "5px 12px", fontSize: "11.5px" }}
                                  >
                                    确认批准
                                  </button>

                                  <button
                                    type="button"
                                    disabled={processingAction}
                                    onClick={() => handleRejectOperations([op.operation_id])}
                                    className="secondary-button"
                                    style={{ padding: "5px 10px", fontSize: "11.5px", color: "var(--red)" }}
                                  >
                                    驳回
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* VIEW 5: 发起新放量战役 (scale_launch) - 3步极简向导与双模式配置 */}
      {/* ========================================================================= */}
      {activeSubView === "launch" && (
        <div className="scale-view-section">
          <div className="strategy-section">
            <div className="strategy-section-header">
              <div className="strategy-section-icon">
                <Rocket />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                <h3 style={{ margin: 0, fontSize: "14px", fontWeight: 750, color: "var(--ink-heading)" }}>
                  意图解析与自动化放量发起向导
                </h3>
                <p style={{ margin: 0, fontSize: "11.5px", color: "var(--muted)" }}>
                  3 步极简向导：选择 ASIN ➔ 设定驾驶模式与安全红线 ➔ 选定战略意图并激活 4 阶段放量 SOP。
                </p>
              </div>
            </div>

            <div className="strategy-section-body" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {/* Step 1: Scope Selection with Marketplace Filter and SKU/ASIN Search */}
              <div className="strategy-field">
                <span className="strategy-field-label">
                  <strong>第 1 步：选择纳管 ASIN 与站点</strong>
                  <span style={{ fontSize: "12px", color: "var(--muted)", fontWeight: 400 }}>
                    支持按站点快速筛选，或按 SKU / ASIN 搜索精确匹配
                  </span>
                </span>

                <div className="scale-launch-scope-toolbar" style={{ marginTop: "8px" }}>
                  {/* 1. Marketplace Filter */}
                  <div className="scale-launch-filter-item">
                    <label className="scale-launch-filter-label">站点筛选</label>
                    <CustomSelect
                      value={launchMarketplaceFilter}
                      onChange={(val) => setLaunchMarketplaceFilter(val)}
                      options={launchMarketplaceOptions}
                      className="scale-launch-mp-select"
                      title="按站点筛选"
                      aria-label="按站点筛选"
                    />
                  </div>

                  {/* 2. Scope Target Selection Dropdown */}
                  <div className="scale-launch-filter-item scale-launch-target-item">
                    <label className="scale-launch-filter-label">
                      目标商品 ({filteredLaunchScopes.length})
                    </label>
                    <CustomSelect
                      value={launchScopeId}
                      onChange={(val) => setLaunchScopeId(val)}
                      options={launchScopeOptions}
                      className="scale-scope-custom-select scale-launch-scope-select"
                      title="选择站点与 ASIN"
                      aria-label="选择站点与 ASIN"
                    />
                  </div>

                  {/* 3. SKU / ASIN Search Input (Empty placeholder) */}
                  <div className="scale-launch-filter-item scale-launch-search-item">
                    <label className="scale-launch-filter-label">SKU / ASIN 搜索</label>
                    <div className="scale-launch-search-wrap">
                      <Search size={14} className="scale-launch-search-icon" />
                      <input
                        type="text"
                        className="scale-launch-search-input"
                        placeholder=""
                        value={launchSearchQuery}
                        onChange={(e) => setLaunchSearchQuery(e.target.value)}
                        aria-label="搜索 SKU 或 ASIN"
                      />
                      {launchSearchQuery && (
                        <button
                          type="button"
                          className="scale-launch-search-clear"
                          onClick={() => setLaunchSearchQuery("")}
                          title="清空搜索"
                          aria-label="清空搜索"
                        >
                          <X size={13} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Reset Filters button if any filter active */}
                  {(launchMarketplaceFilter !== "ALL" || launchSearchQuery.trim() !== "") && (
                    <button
                      type="button"
                      className="secondary-button scale-launch-reset-btn"
                      onClick={() => {
                        setLaunchMarketplaceFilter("ALL");
                        setLaunchSearchQuery("");
                      }}
                      title="重置所有筛选与搜索"
                    >
                      <RotateCcw size={12} /> 重置筛选
                    </button>
                  )}
                </div>

                {/* Selected Product Summary Card */}
                {launchTargetScope && (
                  <div className="scale-launch-selected-card">
                    <div className="scale-launch-selected-card-main">
                      <MarketplaceFlag marketplace={launchTargetScope.marketplace} />
                      <div className="scale-launch-selected-card-info">
                        <div className="scale-launch-selected-card-title-row">
                          <span className="scale-launch-selected-sku">
                            SKU: <strong>{launchTargetScope.sku || "默认"}</strong>
                          </span>
                          <span className="scale-launch-selected-divider">·</span>
                          <span className="scale-launch-selected-asin">
                            ASIN: <strong>{launchTargetScope.child_asin}</strong>
                          </span>
                          <button
                            type="button"
                            className={`scale-fleet-copy-btn ${copiedAsin === launchTargetScope.child_asin ? "copied" : ""}`}
                            onClick={(e) => handleCopyAsin(e, launchTargetScope.child_asin)}
                            title={copiedAsin === launchTargetScope.child_asin ? "已复制到剪贴板" : "复制 ASIN"}
                            aria-label={`复制 ASIN ${launchTargetScope.child_asin}`}
                          >
                            {copiedAsin === launchTargetScope.child_asin ? <Check size={12} /> : <Copy size={12} />}
                            {copiedAsin === launchTargetScope.child_asin && <span className="scale-fleet-copy-toast">已复制</span>}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="scale-launch-selected-card-badges">
                      <span className="scale-launch-currency-badge">
                        币种: <strong>{launchTargetScope.currency || "USD"} ({launchCurrSym})</strong>
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Step 2: Mode Selection & Hard Limits */}
              <div className="strategy-field">
                <span className="strategy-field-label">
                  <strong>第 2 步：选择智能驾驶模式与安全红线</strong>
                </span>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginTop: "8px" }}>
                  {/* Mode Card 1: Fully Autonomous */}
                  <div
                    onClick={() => setLaunchMode("AUTO")}
                    style={{
                      padding: "16px",
                      borderRadius: "var(--radius-md)",
                      border: launchMode === "AUTO" ? "2px solid var(--teal)" : "1px solid var(--line)",
                      background: launchMode === "AUTO" ? "rgba(34, 197, 94, 0.05)" : "var(--surface)",
                      cursor: "pointer"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Rocket size={18} style={{ color: "var(--status-success-ink)" }} />
                      <strong style={{ fontSize: "14px", color: "var(--ink-heading)" }}>全自动模式 (Auto-pilot)</strong>
                    </div>
                    <p style={{ fontSize: "12px", color: "var(--muted)", margin: "6px 0 0", lineHeight: 1.5 }}>
                      AI 深度自动驾驶：出价与拓词调优自动执行并记账；高风险操作（单次预算涨幅 &gt;50% 或暂停主活动）自动触发审批安全护栏。
                    </p>
                  </div>

                  {/* Mode Card 2: Approval Required */}
                  <div
                    onClick={() => setLaunchMode("APPROVAL_REQUIRED")}
                    style={{
                      padding: "16px",
                      borderRadius: "var(--radius-md)",
                      border: launchMode === "APPROVAL_REQUIRED" ? "2px solid #a855f7" : "1px solid var(--line)",
                      background: launchMode === "APPROVAL_REQUIRED" ? "rgba(168, 85, 247, 0.05)" : "var(--surface)",
                      cursor: "pointer"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <ShieldCheck size={18} style={{ color: "#a855f7" }} />
                      <strong style={{ fontSize: "14px", color: "var(--ink-heading)" }}>人工审批模式 (Co-pilot)</strong>
                    </div>
                    <p style={{ fontSize: "12px", color: "var(--muted)", margin: "6px 0 0", lineHeight: 1.5 }}>
                      人机协同模式：AI 自动挖掘大词与测算方案，所有调优均推入驾驶舱待审批队列，人工一键确认或行内微调后执行。
                    </p>
                  </div>
                </div>

                {/* Engine Mode Selection: Local Algorithmic vs Cloud AI */}
                <div style={{ marginTop: "14px" }}>
                  <span className="strategy-field-label" style={{ marginBottom: "6px" }}>
                    <span>方案生成引擎 (GENERATION ENGINE)</span>
                  </span>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    {/* Engine 1: Local */}
                    <div
                      onClick={() => setEngineMode("LOCAL")}
                      style={{
                        padding: "12px 14px",
                        borderRadius: "var(--radius-md)",
                        border: engineMode === "LOCAL" ? "2px solid var(--accent, #3b82f6)" : "1px solid var(--line)",
                        background: engineMode === "LOCAL" ? "rgba(59, 130, 246, 0.05)" : "var(--surface)",
                        cursor: "pointer"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span style={{ fontSize: "16px" }}>🖥️</span>
                          <strong style={{ fontSize: "13px", color: "var(--ink-heading)" }}>本地确定性算法引擎</strong>
                        </div>
                        <span style={{ fontSize: "11px", padding: "1px 6px", borderRadius: "10px", background: "rgba(34, 197, 94, 0.15)", color: "var(--status-success-ink)", fontWeight: 600 }}>推荐 · 极速</span>
                      </div>
                      <p style={{ fontSize: "11px", color: "var(--muted)", margin: "6px 0 0", lineHeight: 1.4 }}>
                        本地毫秒级推演 · 0 Token 消耗 · 100% 财务保本与硬护栏严格数学对齐
                      </p>
                    </div>

                    {/* Engine 2: AI */}
                    <div
                      onClick={() => setEngineMode("AI")}
                      style={{
                        padding: "12px 14px",
                        borderRadius: "var(--radius-md)",
                        border: engineMode === "AI" ? "2px solid #ec4899" : "1px solid var(--line)",
                        background: engineMode === "AI" ? "rgba(236, 72, 153, 0.05)" : "var(--surface)",
                        cursor: "pointer"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span style={{ fontSize: "16px" }}>🤖</span>
                          <strong style={{ fontSize: "13px", color: "var(--ink-heading)" }}>AI 云端大模型引擎</strong>
                        </div>
                        <span style={{ fontSize: "11px", padding: "1px 6px", borderRadius: "10px", background: "rgba(236, 72, 153, 0.15)", color: "#ec4899", fontWeight: 600 }}>深度推演</span>
                      </div>
                      <p style={{ fontSize: "11px", color: "var(--muted)", margin: "6px 0 0", lineHeight: 1.4 }}>
                        调用系统配置的大模型 (OpenAI / DeepSeek / Claude) · 深度语义诊断与 Prompt 溯源
                      </p>
                    </div>
                  </div>
                </div>

                {/* Hard Limits */}
                <div style={{ marginTop: "14px" }}>
                  <span className="strategy-field-label" style={{ marginBottom: "6px" }}>
                    <span>物理硬护栏安全边界 (HARD LIMITS)</span>
                  </span>
                  <div className="strategy-form-grid four">
                    <div className="strategy-field">
                      <span className="strategy-field-label">
                        <span>单日预算上限 ({launchCurrSym})</span>
                      </span>
                      <input
                        type="number"
                        min="1"
                        value={launchBudget}
                        onChange={(e) => setLaunchBudget(Number(e.target.value))}
                        placeholder="如 150"
                      />
                    </div>

                    <div className="strategy-field">
                      <span className="strategy-field-label">
                        <span>单次最高竞价 ({launchCurrSym})</span>
                      </span>
                      <input
                        type="number"
                        step="0.1"
                        min="0.1"
                        value={launchBid}
                        onChange={(e) => setLaunchBid(Number(e.target.value))}
                        placeholder="如 3.0"
                      />
                    </div>

                    <div className="strategy-field">
                      <span className="strategy-field-label">
                        <span>单次调价步长 (%)</span>
                      </span>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        step="1"
                        value={launchBidStep}
                        onChange={(e) => setLaunchBidStep(Number(e.target.value))}
                        placeholder="如 20"
                      />
                    </div>

                    <div className="strategy-field">
                      <span className="strategy-field-label">
                        <span>TACOS 上限 (%)</span>
                      </span>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        step="1"
                        value={launchMaxTacos}
                        onChange={(e) => setLaunchMaxTacos(Number(e.target.value))}
                        placeholder="如 25"
                      />
                    </div>
                  </div>
                  <p style={{ fontSize: "11.5px", color: "var(--muted)", margin: "8px 0 0", display: "flex", alignItems: "center", gap: "6px" }}>
                    <ShieldCheck size={13} style={{ color: "var(--teal)" }} />
                    <span>系统内置硬熔断：安全库存底线默认锁定为 <strong>DOC &lt; 20 天</strong> 自动降级保护（无需手动配置）。</span>
                  </p>
                </div>
              </div>

              {/* Step 3: Intent & Activation */}
              <div className="strategy-field">
                <span className="strategy-field-label">
                  <strong>第 3 步：选定战略意图并激活</strong>
                  <span>输入自然语言放量意图</span>
                </span>

                {/* Natural Language Prompt */}
                <div className="scale-prompt-wrapper" style={{ marginTop: "8px" }}>
                  <input
                    type="text"
                    value={launchPrompt}
                    onChange={(e) => {
                      setLaunchPrompt(e.target.value);
                      setUserPrompt(e.target.value);
                    }}
                    placeholder="例如：把这款产品扩大销量，核心词冲刺首页前 10"
                    className="scale-prompt-input"
                  />
                  <button
                    type="button"
                    disabled={loadingGoal || !launchScopeId}
                    onClick={handleLaunchGoal}
                    className="scale-launch-btn"
                  >
                    {loadingGoal ? <RefreshCw size={14} className="spin" /> : <Zap size={14} />}
                    {loadingGoal ? (engineMode === "AI" ? "🤖 AI 大模型深度推演中 (约1~3分钟)..." : "正在规划...") : (engineMode === "AI" ? "🤖 启动 AI 云端点火" : "⚡ 启动本地极速点火")}
                  </button>
                </div>

                {/* Intent Cards */}
                <div className="scale-intent-grid" style={{ marginTop: "12px" }}>
                  {[
                    { id: "EXPAND_SALES_AGGRESSIVE", title: "🔥 激进放量冲排位", desc: "首页核心大词冲刺，以自然位拉升为核心" },
                    { id: "EXPAND_SALES_STEADY", title: "🛡️ 稳健放量", desc: "以高转化长尾为主，严格控制 ACOS 在 Break-even 内" },
                    { id: "PROFIT_HARVEST", title: "💰 利润收割", desc: "收缩大词竞价，锁定自然排位，最大化净利润" },
                    { id: "LIQUIDATION", title: "📦 清货放量", desc: "加速库存周转，最大化现金回流" }
                  ].map((item) => (
                    <div
                      key={item.id}
                      onClick={() => {
                        setLaunchIntent(item.id as GoalIntentType);
                        setSelectedIntent(item.id as GoalIntentType);
                      }}
                      className={`scale-intent-card ${launchIntent === item.id ? "selected" : ""}`}
                    >
                      <div className="scale-intent-card-title">{item.title}</div>
                      <div className="scale-intent-card-desc">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* In-app Confirm Stop Autopilot Dialog */}
      {scopeToRemove && (
        typeof document !== "undefined" ? createPortal(
          <div
            className="dialog-backdrop"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && !removingScopeId) {
                setScopeToRemove(null);
              }
            }}
          >
            <div className="dialog" role="dialog" aria-modal="true" style={{ maxWidth: "480px" }}>
              <div className="dialog-header">
                <h2>停止 ASIN 自动驾驶</h2>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => setScopeToRemove(null)}
                  disabled={Boolean(removingScopeId)}
                  title="关闭"
                  aria-label="关闭对话框"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="delete-confirmation">
                <div className="delete-warning" style={{ background: "var(--amber-soft, rgba(245, 158, 11, 0.08))", border: "1px solid var(--amber-border, rgba(245, 158, 11, 0.25))", borderRadius: "var(--radius-md)", padding: "14px 16px" }}>
                  <AlertTriangle style={{ color: "var(--amber, #f59e0b)", width: 22, height: 22, flexShrink: 0 }} />
                  <div>
                    <strong style={{ color: "var(--ink-heading)", fontSize: "13.5px" }}>确认移除 {scopeToRemove.child_asin} 的自动驾驶？</strong>
                    <p style={{ marginTop: "4px", fontSize: "12px", color: "var(--muted)", lineHeight: 1.55 }}>
                      将停止该 ASIN 的 AI 巡检、动态出价与拓词调优，并删除自动驾驶专属历史 Runs、计划、AI 结果、执行批次与 MAP 读回记录；<strong>不会删除</strong> ASIN 基础配置、日常系统任务及日常系统历史。
                    </p>
                  </div>
                </div>
                <dl>
                  <div>
                    <dt>ASIN / SKU</dt>
                    <dd>
                      <strong>{scopeToRemove.child_asin}</strong>
                      {scopeToRemove.sku && <span style={{ color: "var(--muted)", fontWeight: 500 }}> / {scopeToRemove.sku}</span>}
                    </dd>
                  </div>
                  <div>
                    <dt>站点</dt>
                    <dd style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                      <MarketplaceFlag marketplace={scopeToRemove.marketplace} />
                      <span>{scopeToRemove.marketplace}</span>
                    </dd>
                  </div>
                </dl>
                <div className="dialog-actions">
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setScopeToRemove(null)}
                    disabled={Boolean(removingScopeId)}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    className="danger-button"
                    onClick={() => void handleConfirmRemoveAutopilot()}
                    disabled={Boolean(removingScopeId)}
                  >
                    {removingScopeId ? <RefreshCw size={14} className="spin" /> : <Trash2 size={14} />}
                    <span>{removingScopeId ? "正在停止..." : "确认停止自动驾驶"}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        ) : null
      )}

      {/* Modal Dialog for Editing Goal Targets & Budget */}
      {isEditingTargets && activeScope && (
        typeof document !== "undefined" ? createPortal(
          <div
            className="dialog-backdrop"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && !savingTargets) {
                setIsEditingTargets(false);
              }
            }}
          >
            <div className="edit-targets-dialog" role="dialog" aria-modal="true">
              {/* Header */}
              <div className="edit-targets-header">
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div className="edit-targets-header-icon">
                    <Target size={20} />
                  </div>
                  <div>
                    <h2 className="edit-targets-header-title">修改自动驾驶放量目标与预算约束</h2>
                    <div className="edit-targets-header-meta">
                      <span className="edit-targets-meta-pill">
                        <MarketplaceFlag marketplace={activeScope.marketplace} />
                        <span>{activeScope.marketplace}</span>
                      </span>
                      <span className="edit-targets-meta-pill mono">
                        {activeScope.child_asin}
                      </span>
                      {activeScope.sku && (
                        <span className="edit-targets-meta-pill" style={{ color: "var(--muted)" }}>
                          SKU: {activeScope.sku}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => setIsEditingTargets(false)}
                  disabled={savingTargets}
                  title="关闭"
                  aria-label="关闭对话框"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Body */}
              <div className="edit-targets-body">
                {editTargetError && (
                  <div className="status-banner danger" style={{ margin: 0, padding: "9px 12px", fontSize: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
                    <AlertTriangle size={15} style={{ flexShrink: 0 }} />
                    <span>{editTargetError}</span>
                  </div>
                )}

                {/* Section 1: Parameters & Guardrails */}
                <div>
                  <div className="edit-targets-section-head">
                    <span className="edit-targets-section-title">
                      <Sliders size={14} style={{ color: "var(--teal)" }} />
                      <span>放量目标与物理安全护栏</span>
                    </span>
                    <span className="edit-targets-section-desc">单品全盘约束 · 触发自动平滑熔断</span>
                  </div>

                  <div className="edit-targets-grid">
                    {/* 1. Target Daily Units */}
                    <div className="edit-targets-card">
                      <div className="edit-targets-card-header">
                        <div className="edit-targets-card-label">
                          <span>冲刺日销目标</span>
                          {overviewMetrics.baselineUnits > 0 && (
                            <div className="edit-targets-presets">
                              {[
                                { label: "+30%", val: Math.max(1, Math.round(overviewMetrics.baselineUnits * 1.3)) },
                                { label: "+50%", val: Math.max(1, Math.round(overviewMetrics.baselineUnits * 1.5)) },
                                { label: "2x", val: Math.max(1, Math.round(overviewMetrics.baselineUnits * 2.0)) },
                                { label: "3x", val: Math.max(1, Math.round(overviewMetrics.baselineUnits * 3.0)) }
                              ].map((preset) => (
                                <button
                                  key={preset.label}
                                  type="button"
                                  className="edit-targets-preset-chip"
                                  onClick={() => setEditTargetUnits(preset.val)}
                                  title={`设为 ${preset.val} 单/天`}
                                >
                                  {preset.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className="edit-targets-card-desc">基于当前基线设定全盘日均出单量</span>
                      </div>

                      <div className="edit-targets-input-wrap">
                        <input
                          type="number"
                          min="1"
                          max="5000"
                          step="1"
                          className="edit-targets-input"
                          value={editTargetUnits}
                          onChange={(e) => setEditTargetUnits(Math.max(1, Number(e.target.value) || 1))}
                          placeholder="如 10"
                        />
                        <span className="edit-targets-input-suffix">单/天</span>
                      </div>

                      <div className="edit-targets-feedback accent">
                        <TrendingUp size={12} style={{ flexShrink: 0 }} />
                        <span>
                          基线 {overviewMetrics.baselineUnits} 单/天 ➔{" "}
                          <strong>
                            {overviewMetrics.baselineUnits > 0
                              ? `${editTargetUnits >= overviewMetrics.baselineUnits ? "+" : ""}${((editTargetUnits / overviewMetrics.baselineUnits - 1) * 100).toFixed(0)}% (${(editTargetUnits / overviewMetrics.baselineUnits).toFixed(1)}x)`
                              : "冷启动爬坡"}
                          </strong>
                        </span>
                      </div>
                    </div>

                    {/* 2. Max Tolerated TACOS */}
                    <div className="edit-targets-card">
                      <div className="edit-targets-card-header">
                        <div className="edit-targets-card-label">
                          <span>放量 TACOS 容忍上限</span>
                        </div>
                        <span className="edit-targets-card-desc">单品全盘总广告费 / 订单总金额</span>
                      </div>

                      <div className="edit-targets-input-wrap">
                        <input
                          type="number"
                          min="1"
                          max="100"
                          step="0.5"
                          className="edit-targets-input"
                          value={editMaxTacos}
                          onChange={(e) => setEditMaxTacos(Number(e.target.value))}
                          placeholder="如 20.0"
                        />
                        <span className="edit-targets-input-suffix">%</span>
                      </div>

                      <div className={`edit-targets-feedback ${editMaxTacos <= Number(overviewMetrics.breakEvenAcos) ? "accent" : "warning"}`}>
                        <Shield size={12} style={{ flexShrink: 0 }} />
                        <span>
                          保本 ACOS {overviewMetrics.breakEvenAcos}% · {editMaxTacos <= Number(overviewMetrics.breakEvenAcos) ? "控费安全区" : "容忍适度亏损放量"}
                        </span>
                      </div>
                    </div>

                    {/* 3. Max Daily Budget */}
                    <div className="edit-targets-card">
                      <div className="edit-targets-card-header">
                        <div className="edit-targets-card-label">
                          <span>全盘日预算硬约束</span>
                        </div>
                        <span className="edit-targets-card-desc">所有在跑活动单日花费总和上限</span>
                      </div>

                      <div className="edit-targets-input-wrap">
                        <span className="edit-targets-input-prefix">{currSym}</span>
                        <input
                          type="number"
                          min="1"
                          max="20000"
                          step="1"
                          className="edit-targets-input"
                          value={editMaxDailyBudget}
                          onChange={(e) => setEditMaxDailyBudget(Number(e.target.value))}
                          placeholder="如 40.00"
                        />
                        <span className="edit-targets-input-suffix">/日</span>
                      </div>

                      <div className="edit-targets-feedback muted">
                        <span>超标触发平滑熔断，优先保障 RANK 冲刺组</span>
                      </div>
                    </div>

                    {/* 4. Max Single Bid */}
                    <div className="edit-targets-card">
                      <div className="edit-targets-card-header">
                        <div className="edit-targets-card-label">
                          <span>单次最高竞价约束</span>
                        </div>
                        <span className="edit-targets-card-desc">任意关键词或投放出价硬天花板</span>
                      </div>

                      <div className="edit-targets-input-wrap">
                        <span className="edit-targets-input-prefix">{currSym}</span>
                        <input
                          type="number"
                          min="0.1"
                          max="100"
                          step="0.05"
                          className="edit-targets-input"
                          value={editMaxSingleBid}
                          onChange={(e) => setEditMaxSingleBid(Number(e.target.value))}
                          placeholder="如 1.20"
                        />
                      </div>

                      <div className="edit-targets-feedback muted">
                        <span>卡位提价不超过此值，防止点击成本失控</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Intent Selector */}
                <div>
                  <div className="edit-targets-section-head">
                    <span className="edit-targets-section-title">
                      <Flame size={14} style={{ color: "var(--amber, #d97706)" }} />
                      <span>战略放量意图模式 (Intent)</span>
                    </span>
                    <span className="edit-targets-section-desc">调整算法出价激进程度与流量侧重点</span>
                  </div>

                  <div className="edit-intent-grid">
                    {[
                      {
                        id: "EXPAND_SALES_AGGRESSIVE" as GoalIntentType,
                        icon: "🔥",
                        title: "激进放量冲排位",
                        tag: "冲自然排名",
                        desc: "主打首页 Top 10 黄金位卡位，加速自然飞轮"
                      },
                      {
                        id: "EXPAND_SALES_STEADY" as GoalIntentType,
                        icon: "🛡️",
                        title: "稳健放量",
                        tag: "保本控费",
                        desc: "高转化长尾为主，保本 ACOS 严格控费"
                      },
                      {
                        id: "PROFIT_HARVEST" as GoalIntentType,
                        icon: "💰",
                        title: "利润收割",
                        tag: "利润优先",
                        desc: "收缩大词竞价，锁定高位自然单转化"
                      },
                      {
                        id: "LIQUIDATION" as GoalIntentType,
                        icon: "📦",
                        title: "清货放量",
                        tag: "加速周转",
                        desc: "以库存周转为首要，快速回流资金"
                      }
                    ].map((item) => {
                      const isSelected = editIntent === item.id;
                      return (
                        <div
                          key={item.id}
                          onClick={() => setEditIntent(item.id)}
                          className={`edit-intent-card ${isSelected ? "selected" : ""}`}
                        >
                          <div className="edit-intent-card-top">
                            <div className="edit-intent-card-title">
                              <span style={{ fontSize: "14px" }}>{item.icon}</span>
                              <span>{item.title}</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <span className="edit-intent-card-tag">{item.tag}</span>
                              <div
                                style={{
                                  width: "15px",
                                  height: "15px",
                                  borderRadius: "50%",
                                  border: `1.5px solid ${isSelected ? "var(--teal)" : "var(--line-strong)"}`,
                                  background: isSelected ? "var(--teal)" : "transparent",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#ffffff"
                                }}
                              >
                                {isSelected && <Check size={10} strokeWidth={3} />}
                              </div>
                            </div>
                          </div>
                          <p className="edit-intent-card-desc">{item.desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="edit-targets-footer">
                <div style={{ fontSize: "11px", color: "var(--muted)", display: "flex", alignItems: "center", gap: "5px" }}>
                  <CheckCircle2 size={13} style={{ color: "var(--teal)", flexShrink: 0 }} />
                  <span>实时写入放量 SOP，下一个算法执行周期生效</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setIsEditingTargets(false)}
                    disabled={savingTargets}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    className="primary-button"
                    onClick={() => void handleSaveTargets()}
                    disabled={savingTargets}
                    style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                  >
                    {savingTargets ? <RefreshCw size={14} className="spin" /> : <Check size={14} />}
                    <span>{savingTargets ? "正在保存..." : "保存并立即生效"}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        ) : null
      )}
    </div>
  );
}
